import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { useRegisterUnsavedChanges } from "../../contexts/UnsavedChangesContext";
import { useToast } from "../../contexts/ToastContext";
import ConfirmDialog from "../../components/ui/ConfirmDialog";
import DatePicker from "../../components/ui/DatePicker";
import {
  selectClinicId,
  selectTherapistId,
  selectTherapistName,
  selectIsAdmin,
  selectTherapistProfile,
  selectClinicProfile,
  selectEnglishCommunicationEnabled,
  selectCanAccessFinance,
  selectAllowMultipleTherapists,
  selectPunchCardsEnabled,
  selectIsNutritionClinic,
  selectClinicManagesCouplesAsPatients,
  selectHasPermission,
  selectClinicSharesAllPatients,
  selectClinicSharesTreatments,
  selectClinicRole,
} from "../../store/slices/authSlice";
import { isAssignableTherapist } from "../../config/permissionsRegistry";
import { emptyPartner, getCouplePartners } from "../../utils/coupleUtils";
import EnglishCommunicationToggle from "../../components/patients/EnglishCommunicationToggle";
import NutritionSettingsSection from "../../components/patients/nutrition/NutritionSettingsSection";
import PersonalPageLink from "../../components/patients/nutrition/PersonalPageLink";
import CouplePartnersFields, {
  CoupleToggle,
} from "../../components/patients/CouplePartnersFields";
import {
  useGetPatientQuery,
  useUpdatePatientMutation,
  useReassignPatientMutation,
  useUpdatePatientTherapistAssignmentsMutation,
  useSetPatientActiveMutation,
} from "../../store/services/patientsApi";
import { getPatientTherapistIds } from "../../firebase";
import { getEffectiveLimits } from "../../services/planLimits";
import useCanListAllPatientTreatments from "../../hooks/useCanListAllPatientTreatments";
import { useGetClinicTherapistsQuery } from "../../store/services/therapistApi";
import {
  useGetTreatmentsForPatientQuery,
  useGetTreatmentTagsQuery,
  useLogTreatmentReadAuditMutation,
} from "../../store/services/treatmentsApi";
import { useGetCategoriesQuery } from "../../store/services/tasksApi";
import {
  useGetPatientTransactionsQuery,
  useCreateTransactionMutation,
  useUpdateTransactionMutation,
} from "../../store/services/financeApi";
import { useGetAppointmentsQuery, calendarApi } from "../../store/services/calendarApi";
import { useGetSignedFormsForPatientQuery } from "../../store/services/formsApi";
import {
  useGetPatientQuestionnaireSubmissionsQuery,
  useResendQuestionnaireMutation,
} from "../../store/services/questionnairesApi";
import QuestionnaireViewModal from "../../components/patients/QuestionnaireViewModal";
import {
  TRANSACTION_STATUS,
  normalizeTransactionStatus,
  generateInitialPatientSummary,
  regenerateSignedFormPdf,
  generateQuestionnairePDF,
} from "../../firebase";
import PaymentSummarySmsCard from "../../components/finance/PaymentSummarySmsCard";
// Grow payment links (grow_integration.md Phase 5.4/5.6)
import useGrowLinksMap from "../../components/grow/useGrowLinksMap";
import GrowLinkStatusChip from "../../components/grow/GrowLinkStatusChip";
import RequestPaymentButton from "../../components/grow/RequestPaymentButton";
import GrowPatientLinksHistory from "../../components/grow/GrowPatientLinksHistory";
import useCancelGrowLinkOnManualPayment from "../../hooks/useCancelGrowLinkOnManualPayment";
import DayTimePicker from "../../components/patients/DayTimePicker";
import {
  formatLocalDateString,
  formatCalendarIsoToDdMmYyyy,
  formatHebrewDate,
  formatTime,
} from "../../utils/dateUtils";
import InvoiceQuickModal from "../../components/morning/InvoiceQuickModal";
import DigitalInvoiceQuickModal from "../../components/digitalInvoice/DigitalInvoiceQuickModal";
import {
  ArrowRight,
  PencilSimple,
  FloppyDisk,
  X,
  User,
  Phone,
  Stethoscope,
  Gear,
  CurrencyCircleDollar,
  HardHat,
  Eye,
  Plus,
  Clock,
  Lock,
  LockOpen,
  Tag,
  CalendarBlank,
  Folder,
  Receipt,
  Check,
  WarningCircle,
  CheckCircle,
  FileText,
  ArrowSquareOut,
  Info,
  Sparkle,
  Toolbox,
  PaperPlaneTilt,
  ListChecks,
  ClockCounterClockwise,
  ChatCircleDots,
  GameController,
  UsersThree,
  ArrowsClockwise,
  FilePdf,
  Copy,
  Archive,
  ArrowUUpLeft,
  Ticket,
  Prohibit,
  DotsThree,
} from "@phosphor-icons/react";
import PatientFilesTab from "../../components/patients/PatientFilesTab";
import EmptyDraw from "../../components/ui/EmptyDraw";
import PunchCardTab from "../../components/patients/punchCards/PunchCardTab";
import PatientAppointmentsDiaryTab from "../../components/patients/PatientAppointmentsDiaryTab";
import ReferralSourceField from "../../components/patients/ReferralSourceField";
import {
  getReferralSourceLabel,
  persistCustomReferralSource,
} from "../../utils/referralSources";
import { HelpButton } from "../../components/shared";
import ContentLoader from "../../components/shared/ContentLoader";
import LoaderLoop from "../../components/shared/loader/LoaderLoop";
import { NoAccessState, ReadOnlyBanner } from "../../components/shared/access";
import ModalCloseButton from "../../components/ui/ModalCloseButton";
import CustomClinikitActionButton from "../../components/ui/CustomClinikitActionButton";
import TabBar from "../../components/ui/TabBar";
import "./PatientProfilePage.css";

const MAIN_TABS = [
  { id: "profile", label: "פרופיל", icon: User },
  { id: "treatments", label: "טיפולים", icon: Stethoscope },
  { id: "appointmentsDiary", label: "יומן פגישות", icon: CalendarBlank },
  { id: "files", label: "תיק מטופל", icon: Folder },
  { id: "formsAndQuestionnaires", label: "טפסים ושאלונים", icon: FileText },
  { id: "payments", label: "תשלומים", icon: CurrencyCircleDollar },
  { id: "punchCard", label: "כרטיסיה", icon: Ticket },
];

/**
 * The nutrition coaching tools moved to מרכז הליווי (/dashboard/nutrition/:id).
 * Old `?tab=` deep links to them redirect there — the tour, notifications and
 * the pending queue all link by tab id.
 */
const NUTRITION_WORKSPACE_TABS = new Set([
  "menus",
  "measurements",
  "dailyReports",
  "nutritionNotes",
]);

const PROFILE_TABS = [
  { id: "basic", label: "פרטים בסיסיים", icon: User },
  { id: "personal", label: "פרטים נוספים", icon: Phone },
  { id: "clinical", label: "רקע רפואי", icon: Stethoscope },
  { id: "admin", label: "ניהול ולו״ז", icon: Gear },
];

const STATUS_CONFIG = {
  active: { label: "פעיל", color: "green" },
  waiting: { label: "בהמתנה", color: "yellow" },
  finished: { label: "סיים טיפול", color: "gray" },
  initial: { label: "פנייה ראשונית", color: "blue" },
};

const GENDER_OPTIONS = [
  { value: "", label: "לא נבחר" },
  { value: "male", label: "זכר" },
  { value: "female", label: "נקבה" },
  { value: "other", label: "אחר" },
];

const LANGUAGE_OPTIONS = [
  "עברית",
  "ערבית",
  "רוסית",
  "אנגלית",
  "אמהרית",
  "צרפתית",
  "ספרדית",
];

const HEALTH_FUND_OPTIONS = [
  { value: "", label: "לא נבחר" },
  { value: "clalit", label: "כללית" },
  { value: "maccabi", label: "מכבי" },
  { value: "meuhedet", label: "מאוחדת" },
  { value: "leumit", label: "לאומית" },
  { value: "private", label: "פרטי" },
];

const STATUS_OPTIONS = [
  { value: "active", label: "פעיל" },
  { value: "waiting", label: "בהמתנה" },
  { value: "finished", label: "סיים טיפול" },
  { value: "initial", label: "פנייה ראשונית" },
];

function PatientProfilePage() {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  const clinicId = useSelector(selectClinicId);
  const currentTherapistId = useSelector(selectTherapistId);
  const currentTherapistName = useSelector(selectTherapistName);
  const isAdmin = useSelector(selectIsAdmin);
  // Permissions v2 (legacy therapists: clinical.viewSensitive / treatments.write
  // resolve TRUE, the rest FALSE — so today's UI is unchanged for them).
  const canReassignPerm = useSelector(selectHasPermission("patients.reassign"));
  const canExportData = useSelector(selectHasPermission("allowExportData"));
  const canEditOthers = useSelector(selectHasPermission("patients.editOthers"));
  const canViewSensitive = useSelector(selectHasPermission("clinical.viewSensitive"));
  const canViewAllTreatments = useSelector(selectHasPermission("treatments.viewAllClinic"));
  const canViewSharedTreatments = useSelector(selectHasPermission("treatments.viewSharedPatient"));
  const canWriteTreatments = useSelector(selectHasPermission("treatments.write"));
  const canViewClinicCalendar = useSelector(selectHasPermission("calendar.viewClinic"));
  const sensitiveHidden = canViewSensitive === false;
  // Multi-clinic v2 Phase 3: "all" patient-sharing mode. Every active member
  // reads/edits every patient (+files); treatments too when the clinic opted
  // in. Legacy / "assigned" clinics resolve false → today's behavior.
  const sharesAllPatients = useSelector(selectClinicSharesAllPatients);
  const sharesTreatments = useSelector(selectClinicSharesTreatments);
  const clinicRole = useSelector(selectClinicRole);
  const [logTreatmentReadAudit] = useLogTreatmentReadAuditMutation();
  const canAccessFinance = useSelector(selectCanAccessFinance);
  const punchCardsEnabled = useSelector(selectPunchCardsEnabled);
  const isNutritionClinic = useSelector(selectIsNutritionClinic);
  const therapistProfile = useSelector(selectTherapistProfile);
  const clinicProfile = useSelector(selectClinicProfile);
  const englishCommunicationEnabled = useSelector(
    selectEnglishCommunicationEnabled
  );
  const clinicManagesCouples = useSelector(selectClinicManagesCouplesAsPatients);
  const allowMultipleTherapists = useSelector(selectAllowMultipleTherapists);
  const { showSuccess, showError } = useToast();

  // "סכם סדרת טיפולים בעזרת AI" — honours NiV overrides like the server quota check
  const aiSummaryEnabled =
    getEffectiveLimits(clinicProfile?.plan, clinicProfile || {}).aiTreatmentsReportEnabled !== false;

  const isUsingGreenInvoice = clinicProfile?.isMorningPaymentSystemIntegrated === true || clinicProfile?.isDigitalInvoiceIntegrated === true;
  const isDigitalInvoiceActive = clinicProfile?.isDigitalInvoiceIntegrated === true;
  const isSpeechTherapist = clinicProfile?.type === "speech_therapist";

  // Get patient types from therapist profile
  const patientTypes = therapistProfile?.patientTypes || [];

  const [activeMainTab, setActiveMainTab] = useState("profile");
  // Header "עוד" dropdown (reassign / deactivate / copy id)
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const moreMenuRef = useRef(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState(null);
  const [coupleErrors, setCoupleErrors] = useState({});
  const [copiedId, setCopiedId] = useState(false);
  const [originalEditData, setOriginalEditData] = useState(null);
  const [showCancelWarning, setShowCancelWarning] = useState(false);

  // Payment modal states
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedAppointmentForPayment, setSelectedAppointmentForPayment] =
    useState(null);
  const [selectedTransactionForPayment, setSelectedTransactionForPayment] =
    useState(null);
  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);

  // Payments tab ledger filter (display only)
  const [paymentsFilter, setPaymentsFilter] = useState("all");

  // Invoice modal state
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [invoiceAppointment, setInvoiceAppointment] = useState(null);

  // Full history modal state
  const [showFullHistory, setShowFullHistory] = useState(false);

  // Questionnaire view modal state
  const [viewingQuestionnaire, setViewingQuestionnaire] = useState(null);
  const [resendingQuestionnaireId, setResendingQuestionnaireId] = useState(null);

  // Track signed-form PDF regeneration state
  const [regeneratingPdfId, setRegeneratingPdfId] = useState(null);

  // Track questionnaire PDF export state (linkId currently being exported)
  const [exportingQuestionnairePdfId, setExportingQuestionnairePdfId] = useState(null);

  // Reassignment state
  const [showReassignModal, setShowReassignModal] = useState(false);
  const [reassignTargetId, setReassignTargetId] = useState("");
  const [reassignTransferAppointments, setReassignTransferAppointments] = useState(null);
  const [isReassigning, setIsReassigning] = useState(false);
  const [multiTherapistSelection, setMultiTherapistSelection] = useState([]);

  // Check if this is an admin viewing from admin patients page
  const isAdminView = searchParams.get("view") === "admin";

  // Track if form has unsaved changes when editing
  const isFormDirty = useMemo(() => {
    if (!isEditing || !editData || !originalEditData) return false;
    return JSON.stringify(editData) !== JSON.stringify(originalEditData);
  }, [isEditing, editData, originalEditData]);

  // Register unsaved changes with global context (handles sidebar navigation blocking)
  useRegisterUnsavedChanges(isFormDirty);

  const {
    data: patient,
    isLoading,
    error,
    refetch: refetchPatient,
  } = useGetPatientQuery(
    { clinicId, patientId },
    { skip: !clinicId || !patientId }
  );

  const [updatePatient, { isLoading: isUpdating }] = useUpdatePatientMutation();
  const [reassignPatient] = useReassignPatientMutation();
  const [updatePatientTherapistAssignments] = useUpdatePatientTherapistAssignmentsMutation();
  const [resendQuestionnaire] = useResendQuestionnaireMutation();
  const [setPatientActive, { isLoading: isTogglingActive }] = useSetPatientActiveMutation();

  // Deactivate/reactivate confirmation dialog state
  const [showInactivityConfirm, setShowInactivityConfirm] = useState(false);

  // Admin / "patients.reassign" holders can reassign patients in multi-therapist clinics
  const canReassign = (isAdmin || canReassignPerm) && !clinicProfile?.isSoloClinic;

  // Shared-patients clinics also load the list for the "מטפל/ת ראשי/ת" line.
  const { data: clinicTherapists = [] } = useGetClinicTherapistsQuery(
    { clinicId },
    {
      skip:
        !clinicId ||
        (!canReassign && !allowMultipleTherapists && !sharesAllPatients),
    }
  );

  // The patient's PRIMARY therapist reads the full treatment history in every
  // clinic (rules allow it regardless of sharing mode).
  const isPrimaryTherapist =
    !!patient?.therapistId && patient.therapistId === currentTherapistId;
  // Co-therapist of THIS patient (rules: isCoTherapistForTreatments also needs
  // treatments.viewSharedPatient, legacy default true).
  const isCoTherapistOfPatient =
    Array.isArray(patient?.therapistIds) &&
    patient.therapistIds.includes(currentTherapistId);
  // Unfiltered treatment list only where rules grant it; everyone else is
  // scoped to their own reports (prevents permission-denied CK-7100 spam).
  // Shared with AiSummaryPage so both pages hit the same RTK cache entry.
  const canListAllPatientTreatments = useCanListAllPatientTreatments(patient);

  const activeTherapists = useMemo(
    () => clinicTherapists.filter(isAssignableTherapist),
    [clinicTherapists]
  );

  // AI Summary regeneration state
  const [isRegeneratingSummary, setIsRegeneratingSummary] = useState(false);

  // Treatments queries
  // Co-therapists of this patient (multi-therapist clinics) see all treatments;
  // other viewers are scoped to their own reports.
  const { data: treatments = [], isLoading: isLoadingTreatments } =
    useGetTreatmentsForPatientQuery(
      {
        clinicId,
        patientId,
        therapistId: canListAllPatientTreatments ? null : currentTherapistId,
      },
      // Viewers without clinical.viewSensitive see NoAccessState on the tab —
      // don't fire the query at all for them.
      { skip: !clinicId || !patientId || sensitiveHidden }
    );

  // Soft-deleted reports stay in `treatments` (rendered as muted rows in the
  // timeline) but are excluded from counts, history, and appointment linking.
  const activeTreatments = useMemo(
    () => treatments.filter((t) => t.isDeleted !== true),
    [treatments]
  );

  const { data: treatmentTypes = [] } = useGetCategoriesQuery(
    { clinicId, type: "treatmentType" },
    { skip: !clinicId }
  );

  const { data: treatmentTags = [] } = useGetTreatmentTagsQuery(
    { clinicId },
    { skip: !clinicId }
  );

  // Signed forms query
  const { data: signedForms = [], isLoading: isLoadingSignedForms } =
    useGetSignedFormsForPatientQuery(
      { clinicId, patientId },
      // signedForms reads require clinical.viewSensitive (rules) — the tab
      // renders NoAccessState for those viewers, so don't fire the query.
      { skip: !clinicId || !patientId || sensitiveHidden }
    );

  // Questionnaire submissions query
  const { data: questionnaireSubmissions = [], isLoading: isLoadingQuestionnaireSubmissions } =
    useGetPatientQuestionnaireSubmissionsQuery(
      { clinicId, patientId },
      { skip: !clinicId || !patientId || sensitiveHidden }
    );

  // Patient transactions query
  // For multi-therapist clinics with non-admin therapist, use Cloud Function
  // to bypass Firestore rules that can't evaluate co-therapist list queries
  const needsSecureFetch = allowMultipleTherapists && !isAdmin;
  const { data: patientTransactions = [], isLoading: isLoadingTransactions } =
    useGetPatientTransactionsQuery(
      {
        clinicId,
        patientId,
        therapistId: isAdmin || allowMultipleTherapists ? null : currentTherapistId,
        useSecure: needsSecureFetch,
      },
      { skip: !clinicId || !patientId }
    );

  // Grow: links behind this patient's transactions + manual-paid guard
  const growLinks = useGrowLinksMap(patientTransactions);
  const growGuard = useCancelGrowLinkOnManualPayment();

  // Future appointments for this patient (used by reassignment modal)
  const nowDate = useMemo(() => new Date().toISOString(), []);
  const futureDate = useMemo(() => {
    const d = new Date();
    d.setFullYear(d.getFullYear() + 1);
    return d.toISOString();
  }, []);

  const { data: futureAppointmentsAll = [] } = useGetAppointmentsQuery(
    {
      clinicId,
      startDate: nowDate,
      endDate: futureDate,
      therapistId: patient?.therapistId || null,
    },
    { skip: !clinicId || !patientId || !patient?.therapistId || !canReassign }
  );

  const patientFutureAppointments = useMemo(
    () => futureAppointmentsAll.filter(
      (apt) => apt.patientId === patientId && apt.status !== "cancelled"
    ),
    [futureAppointmentsAll, patientId]
  );

  const hasFutureAppointments = patientFutureAppointments.length > 0;

  // Create transaction mutation
  const [createTransaction] = useCreateTransactionMutation();
  const [updateTransaction] = useUpdateTransactionMutation();

  // Get patient's completed appointments (last 6 months) to find unpaid ones
  // Both bounds memoized — a fresh ISO string per render would change the
  // RTK Query cache key every render and refetch continuously.
  const { sixMonthsAgo, appointmentsEndDate } = useMemo(() => {
    const now = new Date();
    const date = new Date(now);
    date.setMonth(date.getMonth() - 6);
    return {
      sixMonthsAgo: date.toISOString(),
      appointmentsEndDate: now.toISOString(),
    };
  }, []);

  const { data: patientAppointments = [], isLoading: isLoadingAppointments } =
    useGetAppointmentsQuery(
      {
        clinicId,
        startDate: sixMonthsAgo,
        endDate: appointmentsEndDate,
        therapistId: isAdmin ? null : currentTherapistId,
      },
      { skip: !clinicId || !patientId }
    );

  // Create a map of appointments by ID for quick lookup
  const appointmentsMap = useMemo(() => {
    if (!patientAppointments) return new Map();
    return new Map(
      patientAppointments
        .filter((apt) => apt.patientId === patientId)
        .map((apt) => [apt.id, apt])
    );
  }, [patientAppointments, patientId]);

  // Filter appointments for this patient and find ones without payment
  const unpaidAppointments = useMemo(() => {
    if (!patientAppointments || !patientTransactions) return [];

    // Get all appointment IDs that have transactions
    const appointmentIdsWithTransactions = new Set(
      patientTransactions
        .filter((tx) => tx.appointmentId)
        .map((tx) => tx.appointmentId)
    );

    // Filter patient's completed appointments without transactions
    return patientAppointments
      .filter(
        (apt) =>
          apt.patientId === patientId &&
          apt.status === "completed" &&
          !appointmentIdsWithTransactions.has(apt.id)
      )
      .sort((a, b) => new Date(b.startTime) - new Date(a.startTime));
  }, [patientAppointments, patientTransactions, patientId]);

  // Helper to check if an appointment has an invoice
  const appointmentHasInvoice = (appointmentId) => {
    if (!appointmentId) return false;
    const apt = appointmentsMap.get(appointmentId);
    if (!apt) return false;
    return (
      apt.isProducedReceipitForThisMeeting === true ||
      (apt.RecepitsDocsRefArray && apt.RecepitsDocsRefArray.length > 0)
    );
  };

  // Get invoice info for an appointment
  const getAppointmentInvoiceInfo = (appointmentId) => {
    if (!appointmentId) return null;
    const apt = appointmentsMap.get(appointmentId);
    if (!apt) return null;
    if (apt.RecepitsDocsRefArray && apt.RecepitsDocsRefArray.length > 0) {
      return apt.RecepitsDocsRefArray[0]; // Return first invoice
    }
    return null;
  };

  // Helper to check if a transaction has an invoice
  // First checks the transaction's own RecepitsDocsRefArray, then falls back to the linked appointment
  const transactionHasInvoice = (transaction) => {
    // Check transaction's own invoice reference
    if (
      transaction.RecepitsDocsRefArray &&
      transaction.RecepitsDocsRefArray.length > 0
    ) {
      return true;
    }
    // Fallback to linked appointment
    if (transaction.appointmentId) {
      return appointmentHasInvoice(transaction.appointmentId);
    }
    return false;
  };

  // Get invoice info for a transaction
  // First checks the transaction's own RecepitsDocsRefArray, then falls back to the linked appointment
  const getTransactionInvoiceInfo = (transaction) => {
    // Check transaction's own invoice reference first
    if (
      transaction.RecepitsDocsRefArray &&
      transaction.RecepitsDocsRefArray.length > 0
    ) {
      return transaction.RecepitsDocsRefArray[0]; // Return first invoice
    }
    // Fallback to linked appointment
    if (transaction.appointmentId) {
      return getAppointmentInvoiceInfo(transaction.appointmentId);
    }
    return null;
  };

  // Check for tab query param
  useEffect(() => {
    const tab = searchParams.get("tab");
    if (tab === "treatments") {
      setActiveMainTab("treatments");
    } else if (tab === "files") {
      setActiveMainTab("files");
    } else if (tab === "appointmentsDiary" || tab === "appointments") {
      setActiveMainTab("appointmentsDiary");
    } else if (tab === "payments") {
      setActiveMainTab("payments");
    } else if (tab === "punchCard") {
      setActiveMainTab("punchCard");
    } else if (NUTRITION_WORKSPACE_TABS.has(tab)) {
      // Legacy deep link — the tool now lives on מרכז הליווי
      if (isNutritionClinic && patientId) {
        navigate(`/dashboard/nutrition/${patientId}?tab=${tab}`, { replace: true });
      }
    } else if (tab === "formsAndQuestionnaires" || tab === "forms") {
      setActiveMainTab("formsAndQuestionnaires");
    }
  }, [searchParams, isNutritionClinic, patientId, navigate]);

  // Multi-therapist: check if current user is one of the patient's therapists
  const patientTherapistIds = useMemo(
    () => (patient ? getPatientTherapistIds(patient) : []),
    [patient]
  );
  const isAssignedTherapist = patientTherapistIds.includes(currentTherapistId);
  const hasMultipleTherapists = patientTherapistIds.length > 1;

  // Determine if user can edit this patient
  const isOwnPatient = patient?.therapistId === currentTherapistId || isAssignedTherapist;
  // "all" sharing mode: any active member edits (rules allow it)
  const canEdit = isOwnPatient || isAdmin || canEditOthers || sharesAllPatients;
  const isReadOnly = !canEdit;
  // Treatment reports: assigned therapist, admin, treatments.viewAllClinic,
  // clinic-wide treatment sharing, or the patient's primary therapist.
  const canViewPatientTreatments =
    isOwnPatient ||
    isAdmin ||
    canViewAllTreatments ||
    sharesTreatments ||
    isPrimaryTherapist;
  // Files (תיק מטופל): rules allow the patient's therapist (primary /
  // therapistIds), admins, and every member in "all" sharing mode — NOT
  // treatments.viewAllClinic / treatments sharing, so don't widen on those.
  const canViewPatientFiles = isOwnPatient || isAdmin || sharesAllPatients;
  // Appointments diary: rules allow owner / co-therapist / admin /
  // calendar.viewClinic / all-mode. The diary tab skips its query otherwise.
  const canViewPatientAppointments =
    isOwnPatient || isAdmin || canViewClinicCalendar || sharesAllPatients;
  // All future appointments for this patient across all therapists.
  // Queries by patientId (rules verify owner / co-therapist / admin /
  // calendar.viewClinic / all-mode — exactly canViewPatientAppointments).
  // Feeds the "הפגישה הבאה" fact tile and the multi-therapist card.
  const { data: allPatientAppointments = [] } = useGetAppointmentsQuery(
    { clinicId, startDate: nowDate, endDate: futureDate, patientId },
    { skip: !clinicId || !patientId || !canViewPatientAppointments }
  );

  const patientAllFutureAppointments = useMemo(
    () => allPatientAppointments
      .filter((apt) => apt.status !== "cancelled")
      .sort((a, b) => new Date(a.startTime) - new Date(b.startTime)),
    [allPatientAppointments]
  );

  // Close the header "עוד" menu on outside click / tap
  useEffect(() => {
    if (!showMoreMenu) return undefined;
    const onDown = (e) => {
      if (moreMenuRef.current && !moreMenuRef.current.contains(e.target)) {
        setShowMoreMenu(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
    };
  }, [showMoreMenu]);

  // Header line: who the primary therapist is (shown when the viewer isn't).
  const primaryTherapistName = useMemo(() => {
    if (!patient?.therapistId) return "";
    const t = clinicTherapists.find((ct) => ct.uid === patient.therapistId);
    return t
      ? `${t.firstName || ""} ${t.lastName || ""}`.trim()
      : patient.therapistName || "";
  }, [clinicTherapists, patient?.therapistId, patient?.therapistName]);

  // Multi-clinic v2 Phase 3: read audit when the full-history modal renders
  // other therapists' reports inline (the timeline cards only link to
  // TreatmentDocPage, which audits on its own). The mutation no-ops for the
  // author and debounces repeats client-side.
  useEffect(() => {
    if (!showFullHistory || !clinicId || !currentTherapistId) return;
    activeTreatments.forEach((t) => {
      if (!t?.id || !t.therapistId || t.therapistId === currentTherapistId) return;
      logTreatmentReadAudit({
        clinicId,
        view: {
          treatmentId: t.id,
          patientId,
          ownerTherapistId: t.therapistId,
          context: "profile",
        },
        viewer: {
          viewerId: currentTherapistId,
          viewerName: currentTherapistName,
          viewerRole: clinicRole,
        },
      });
    });
    // Only re-fire when the modal opens (not on every treatments refetch).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showFullHistory, clinicId, currentTherapistId, patientId]);

  const handleBack = () => {
    if (isAdminView) {
      navigate("/dashboard/admin/patients");
    } else {
      navigate("/dashboard/patients");
    }
  };

  // Fetch future, non-cancelled appointments for this patient when the
  // deactivate dialog is open, so the admin can be warned about them.
  const inactivityRangeStart = useMemo(() => new Date().toISOString(), [showInactivityConfirm]); // eslint-disable-line react-hooks/exhaustive-deps
  const inactivityRangeEnd = useMemo(() => {
    const d = new Date();
    d.setFullYear(d.getFullYear() + 2);
    return d.toISOString();
  }, [showInactivityConfirm]); // eslint-disable-line react-hooks/exhaustive-deps
  const {
    data: futureAppointmentsRaw = [],
    isFetching: isFetchingInactivityAppointments,
    isError: isInactivityAppointmentsError,
  } = useGetAppointmentsQuery(
    {
      clinicId,
      patientId,
      startDate: inactivityRangeStart,
      endDate: inactivityRangeEnd,
    },
    { skip: !clinicId || !patientId || !showInactivityConfirm || patient?.isActive === false }
  );
  const futureAppointmentsForInactivity = useMemo(() => {
    return (futureAppointmentsRaw || [])
      .filter((a) => a.status !== "cancelled" && a.status !== "completed")
      .sort((a, b) => new Date(a.startTime) - new Date(b.startTime));
  }, [futureAppointmentsRaw]);

  // Outstanding (awaiting payment) transactions — shown as a heads-up, never blocking.
  const pendingPaymentsForInactivity = useMemo(() => {
    const pending = (patientTransactions || []).filter(
      (tx) => normalizeTransactionStatus(tx.status) === TRANSACTION_STATUS.PENDING
    );
    return {
      count: pending.length,
      total: pending.reduce((sum, tx) => sum + (tx.amount || 0), 0),
    };
  }, [patientTransactions]);

  // Deactivation is blocked while the patient still has future appointments.
  // While the check is still running (or failed) we also block, so the guard
  // can't be bypassed by confirming before the data lands.
  const isDeactivating = patient?.isActive !== false;
  const deactivationCheckPending =
    isDeactivating &&
    (isFetchingInactivityAppointments || isInactivityAppointmentsError);
  const deactivationBlocked =
    isDeactivating && futureAppointmentsForInactivity.length > 0;

  const handleToggleActiveClick = () => {
    setShowInactivityConfirm(true);
  };

  const handleCopyPatientId = async () => {
    if (!patientId) return;
    try {
      await navigator.clipboard.writeText(patientId);
      setCopiedId(true);
      setTimeout(() => setCopiedId(false), 1500);
    } catch (err) {
      console.error("[CK-2081] Failed to copy patient ID", {
        errorMessage: err?.message,
        timestamp: new Date().toISOString(),
      });
    }
  };

  const handleConfirmToggleActive = async () => {
    if (!patient) return;
    const nextIsActive = patient.isActive === false; // currently inactive → reactivate
    // Guard: never deactivate a patient that still has future appointments.
    if (!nextIsActive && (deactivationBlocked || deactivationCheckPending)) {
      showError(
        "לא ניתן להשבית מטופל עם תורים עתידיים. יש לבטל אותם ביומן תחילה."
      );
      return;
    }
    try {
      await setPatientActive({
        clinicId,
        patientId,
        isActive: nextIsActive,
        therapistId: currentTherapistId,
        therapistName: currentTherapistName,
      }).unwrap();
      showSuccess(nextIsActive ? "המטופל הופעל מחדש" : "המטופל הושבת");
    } catch (err) {
      showError(err?.message || "שגיאה בעדכון סטטוס המטופל");
    } finally {
      setShowInactivityConfirm(false);
    }
  };

  const handleEditClick = () => {
    const initialEditData = {
      ...patient,
      address: patient.address || { city: "", street: "", number: "" },
      parent1: { name: "", phone: "", id: "", ...(patient.parent1 || {}) },
      parent2: { name: "", phone: "", id: "", ...(patient.parent2 || {}) },
      isCouple: patient.isCouple === true,
      partner1: { ...emptyPartner(), ...(patient.partner1 || {}) },
      partner2: { ...emptyPartner(), ...(patient.partner2 || {}) },
      smsPrimaryPartner: patient.smsPrimaryPartner === 2 ? 2 : 1,
      patientTypeIds: patient.patientTypeIds || [],
      emergencyContact: patient.emergencyContact || {
        name: "",
        relation: "",
        phone: "",
      },
      languages: patient.languages || [],
      preferredSchedule: patient.preferredSchedule || {},
    };
    setEditData(initialEditData);
    setOriginalEditData(initialEditData);
    setIsEditing(true);
  };

  // Auto-open edit mode when arriving with ?edit=true (e.g. from missing-details alert in send-form modals)
  const autoEditTriggeredRef = useRef(false);
  useEffect(() => {
    if (autoEditTriggeredRef.current) return;
    if (searchParams.get("edit") === "true" && patient) {
      autoEditTriggeredRef.current = true;
      handleEditClick();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, patient]);

  const handleCancelEdit = useCallback(() => {
    // If form has unsaved changes, show warning dialog
    if (isFormDirty) {
      setShowCancelWarning(true);
    } else {
      // No changes, just close
      setEditData(null);
      setOriginalEditData(null);
      setIsEditing(false);
    }
  }, [isFormDirty]);

  const handleConfirmCancelEdit = useCallback(() => {
    setShowCancelWarning(false);
    setEditData(null);
    setOriginalEditData(null);
    setIsEditing(false);
  }, []);

  // Reassign patient handler
  const handleOpenReassign = () => {
    if (allowMultipleTherapists) {
      setMultiTherapistSelection([...patientTherapistIds]);
    } else {
      setReassignTargetId("");
      setReassignTransferAppointments(hasFutureAppointments ? null : false);
    }
    setShowReassignModal(true);
  };

  const handleConfirmReassign = async () => {
    if (!reassignTargetId) return;
    if (hasFutureAppointments && reassignTransferAppointments === null) return;

    setIsReassigning(true);
    try {
      const targetTherapist = activeTherapists.find((t) => t.uid === reassignTargetId);
      const targetName = targetTherapist
        ? `${targetTherapist.firstName} ${targetTherapist.lastName}`
        : "";

      await reassignPatient({
        clinicId,
        patientId,
        patientName: `${patient.firstName} ${patient.lastName}`,
        newTherapistId: reassignTargetId,
        newTherapistName: targetName,
        transferAppointments: reassignTransferAppointments,
        therapistId: currentTherapistId,
        therapistName: currentTherapistName,
      }).unwrap();

      dispatch(
        calendarApi.util.invalidateTags([{ type: "Appointments", id: "LIST" }])
      );
      showSuccess(
        `המטופל/ת שויך/ה בהצלחה ל${targetName}`
      );
      setShowReassignModal(false);
      refetchPatient();
    } catch (error) {
      console.error("[CK-2010] Error reassigning patient:", error);
      showError("שגיאה בשיוך המטופל/ת. נסה שוב.");
    } finally {
      setIsReassigning(false);
    }
  };

  // Multi-therapist confirm handler
  const handleConfirmMultiTherapist = async () => {
    if (multiTherapistSelection.length === 0) return;

    setIsReassigning(true);
    try {
      await updatePatientTherapistAssignments({
        clinicId,
        patientId,
        patientName: `${patient.firstName} ${patient.lastName}`,
        therapistIds: multiTherapistSelection,
        therapistId: currentTherapistId,
        therapistName: currentTherapistName,
      }).unwrap();

      showSuccess("רשימת המטפלים עודכנה בהצלחה");
      setShowReassignModal(false);
      refetchPatient();
    } catch (error) {
      console.error("[CK-2011] Error updating patient therapists:", error);
      showError("שגיאה בעדכון רשימת המטפלים. נסה שוב.");
    } finally {
      setIsReassigning(false);
    }
  };

  const toggleTherapistSelection = (therapistUid) => {
    setMultiTherapistSelection((prev) => {
      if (prev.includes(therapistUid)) {
        if (prev.length <= 1) return prev;
        return prev.filter((id) => id !== therapistUid);
      }
      return [...prev, therapistUid];
    });
  };

  // Get therapist name by ID — searches all clinic therapists (including inactive)
  const getTherapistNameById = (id) => {
    const therapist = clinicTherapists.find((t) => t.uid === id);
    return therapist ? `${therapist.firstName} ${therapist.lastName}` : "";
  };

  // Fields that affect the patient summary and should trigger regeneration
  const SUMMARY_AFFECTING_FIELDS = [
    "birthDate",
    "gender",
    "languages",
    "referralReason",
    "diagnoses",
    "allergies",
    "medications",
    "previousTreatments",
    "clinicalNotes",
    "educationalFramework",
    "hearingTestResults",
  ];

  // Helper to calculate age
  const calculatePatientAge = (birthDate) => {
    if (!birthDate) return null;
    const birth = new Date(birthDate);
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birth.getDate())
    ) {
      age--;
    }
    return age;
  };

  // Helper to format gender to Hebrew
  const formatPatientGender = (gender) => {
    switch (gender) {
      case "male":
        return "זכר";
      case "female":
        return "נקבה";
      case "other":
        return "אחר";
      default:
        return "לא צוין";
    }
  };

  // Check if any summary-affecting fields have changed
  const hasSummaryFieldsChanged = useCallback(() => {
    if (!editData || !originalEditData) return false;
    return SUMMARY_AFFECTING_FIELDS.some(
      (field) =>
        JSON.stringify(editData[field]) !==
        JSON.stringify(originalEditData[field])
    );
  }, [editData, originalEditData]);

  const handleSave = async () => {
    if (!isValidId(editData.parent1?.id) || !isValidId(editData.parent2?.id)) {
      showError("תעודת זהות חייבת להכיל בדיוק 9 ספרות");
      return;
    }
    if (editData.isCouple !== true) {
      // Same required fields as the create-patient wizard
      if (!editData.firstName?.trim() || !editData.lastName?.trim()) {
        showError("יש למלא שם פרטי ושם משפחה");
        return;
      }
      const phoneDigits = String(editData.phone || "").replace(/\D/g, "");
      if (phoneDigits.length !== 10) {
        showError("מספר טלפון צריך להיות 10 ספרות");
        return;
      }
    }
    if (editData.isCouple === true) {
      const newCoupleErrors = {};
      ["partner1", "partner2"].forEach((key) => {
        const partner = editData[key] || {};
        if (!partner.firstName?.trim()) {
          newCoupleErrors[`${key}FirstName`] = "שדה חובה";
        }
        if (!partner.lastName?.trim()) {
          newCoupleErrors[`${key}LastName`] = "שדה חובה";
        }
        if (!partner.phone?.trim()) {
          newCoupleErrors[`${key}Phone`] = "שדה חובה";
        } else if (partner.phone.length !== 10) {
          newCoupleErrors[`${key}Phone`] = "מספר טלפון צריך להיות 10 ספרות";
        }
        if (partner.idNumber && !/^\d{9}$/.test(partner.idNumber)) {
          newCoupleErrors[`${key}Id`] = "תעודת זהות חייבת להכיל בדיוק 9 ספרות";
        }
      });
      if (Object.keys(newCoupleErrors).length > 0) {
        setCoupleErrors(newCoupleErrors);
        showError("יש להשלים את פרטי שני בני הזוג");
        return;
      }
    }
    setCoupleErrors({});
    try {
      // Viewers without clinical.viewSensitive never write medical-history
      // fields (they're masked in the UI, so editData holds no real values).
      let patientDataToSave = editData;
      if (sensitiveHidden) {
        const {
          clinicalNotes: _omitClinicalNotes,
          diagnoses: _omitDiagnoses,
          medications: _omitMedications,
          allergies: _omitAllergies,
          previousTreatments: _omitPreviousTreatments,
          hearingTestResults: _omitHearingTestResults,
          ...rest
        } = editData;
        patientDataToSave = rest;
      }
      await updatePatient({
        clinicId,
        patientId,
        patientData: patientDataToSave,
        therapistId: currentTherapistId,
        therapistName: currentTherapistName,
      }).unwrap();

      // Persist any custom referral source to the therapist's saved list for next time
      await persistCustomReferralSource({
        value: editData.referralSource,
        therapistId: currentTherapistId,
        therapistProfile,
        dispatch,
      });

      // Check if clinical/personal fields changed - regenerate/generate summary if enabled
      if (clinicProfile?.enableAiPatientSummary === true && hasSummaryFieldsChanged()) {
        setIsRegeneratingSummary(true);
        try {
          // Prepare sanitized data for AI (no PII!)
          const sanitizedData = {
            age: calculatePatientAge(editData.birthDate),
            gender: formatPatientGender(editData.gender),
            languages: editData.languages || [],
            referralReason: editData.referralReason || null,
            diagnoses: sensitiveHidden ? null : editData.diagnoses || null,
            allergies: sensitiveHidden ? null : editData.allergies || null,
            medications: sensitiveHidden ? null : editData.medications || null,
            previousTreatments: sensitiveHidden ? null : editData.previousTreatments || null,
            clinicalNotes: sensitiveHidden ? null : editData.clinicalNotes || null,
            educationalFramework: editData.educationalFramework || null,
            hearingTestResults: sensitiveHidden ? null : editData.hearingTestResults || null,
          };

          const summaryResult = await generateInitialPatientSummary({
            clinicId,
            patientData: sanitizedData,
          });

          // If summary generated successfully, save it to the patient
          if (summaryResult.success && summaryResult.summary) {
            await updatePatient({
              clinicId,
              patientId,
              patientData: {
                patientSummary: summaryResult.summary,
              },
              therapistId: currentTherapistId,
              therapistName: currentTherapistName,
            }).unwrap();
          }

          // Refetch patient to get updated summary
          await refetchPatient();

        } catch (summaryError) {
          console.error(
            "[CK-2003] Failed to regenerate patient summary:",
            summaryError
          );
          // Don't show error to user - summary regeneration is non-critical
        } finally {
          setIsRegeneratingSummary(false);
        }
      }

      setIsEditing(false);
      setEditData(null);
      setOriginalEditData(null);
      showSuccess("פרטי המטופל נשמרו בהצלחה");
    } catch (error) {
      console.error("[CK-2002] Error updating patient:", {
        errorCode: "CK-2002",
        errorMessage: error.message,
        clinicId,
        patientId,
        timestamp: new Date().toISOString(),
      });
      showError("שגיאה בשמירת פרטי המטופל");
    }
  };

  const handleChange = (field, value) => {
    setEditData((prev) => ({ ...prev, [field]: value }));
  };

  const handleNestedChange = (parent, field, value) => {
    setEditData((prev) => ({
      ...prev,
      [parent]: { ...prev[parent], [field]: value },
    }));
  };

  const PARTNER_ERROR_SUFFIX = {
    firstName: "FirstName",
    lastName: "LastName",
    phone: "Phone",
    idNumber: "Id",
  };

  const handlePartnerChange = (partnerKey, field, value) => {
    handleNestedChange(partnerKey, field, value);
    const errKey = `${partnerKey}${PARTNER_ERROR_SUFFIX[field]}`;
    if (coupleErrors[errKey]) {
      setCoupleErrors((prev) => ({ ...prev, [errKey]: null }));
    }
  };

  const handleCoupleToggleEdit = (checked) => {
    setEditData((prev) => {
      if (checked) {
        // Prefill partner 1 from the current single-patient fields unless a
        // previous couple's partner data is stored (re-enabling restores it)
        const hasPartnerData = prev.partner1?.firstName || prev.partner1?.phone;
        const partner1 = hasPartnerData
          ? prev.partner1
          : {
              ...emptyPartner(),
              firstName: prev.firstName || "",
              lastName: prev.lastName || "",
              phone: prev.phone || "",
              idNumber: prev.idNumber || "",
            };
        return { ...prev, isCouple: true, partner1 };
      }
      // Turning off: keep partner data stored (hidden), restore the single
      // fields from the SMS-primary partner
      const primary =
        prev.smsPrimaryPartner === 2 ? prev.partner2 : prev.partner1;
      return {
        ...prev,
        isCouple: false,
        firstName: primary?.firstName || prev.firstName,
        lastName: primary?.lastName || prev.lastName,
        phone: primary?.phone || prev.phone,
        idNumber: primary?.idNumber || prev.idNumber || null,
      };
    });
    setCoupleErrors({});
  };

  const handleLanguageToggle = (lang) => {
    setEditData((prev) => ({
      ...prev,
      languages: prev.languages?.includes(lang)
        ? prev.languages.filter((l) => l !== lang)
        : [...(prev.languages || []), lang],
    }));
  };

  const formatPhone = (value) => {
    const digits = value.replace(/\D/g, "");
    return digits.slice(0, 10);
  };

  const formatId = (value) => {
    const digits = value.replace(/\D/g, "");
    return digits.slice(0, 9);
  };

  const isValidId = (value) => !value || /^\d{9}$/.test(value);

  const formatDate = (date) => {
    if (!date) return "—";
    return new Date(date).toLocaleDateString("he-IL", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  const formatDateForInput = (date) => {
    if (!date) return "";
    const d = new Date(date);
    return formatLocalDateString(d); // Use Israel timezone
  };

  const getLabel = (value, options) => {
    const option = options.find((o) => o.value === value);
    return option?.label || "—";
  };

  const formatSchedule = (schedule) => {
    if (!schedule || Object.keys(schedule).length === 0) return "—";
    const dayNames = {
      sunday: "ראשון",
      monday: "שני",
      tuesday: "שלישי",
      wednesday: "רביעי",
      thursday: "חמישי",
      friday: "שישי",
      saturday: "שבת",
    };
    return Object.entries(schedule)
      .map(
        ([day, times]) => `${dayNames[day]}: ${times.from}:00-${times.to}:00`
      )
      .join(" | ");
  };

  if (isLoading) {
    return (
      <div className="patient-profile-page">
        <ContentLoader fill message="טוען פרטי מטופל..." />
      </div>
    );
  }

  if (error || !patient) {
    const errMsg = String(error?.message || error?.error || error || "").toLowerCase();
    const isPermissionError =
      !!error &&
      (errMsg.includes("permission") ||
        errMsg.includes("insufficient") ||
        error?.code === "permission-denied");
    if (isPermissionError) {
      return (
        <div className="patient-profile-page">
          <NoAccessState
            size="page"
            message="המטופל/ת אינו/ה משויך/ת אליך יותר, או שאין לך הרשאה לצפות בכרטיס זה."
          >
            <button className="back-btn" onClick={handleBack}>
              חזור לרשימה
            </button>
          </NoAccessState>
        </div>
      );
    }
    return (
      <div className="patient-profile-page">
        <div className="error-state">
          <p>שגיאה בטעינת פרטי המטופל</p>
          <button onClick={handleBack}>חזור לרשימה</button>
        </div>
      </div>
    );
  }

  const data = isEditing ? editData : patient;
  const status = STATUS_CONFIG[patient.status] || STATUS_CONFIG.active;

  // Basic Tab Content
  const renderBasicTab = () => (
    <div className="profile-section">
      {/* AI Patient Summary Box */}
      {clinicProfile?.enableAiPatientSummary === true && patient?.patientSummary && (
        <div className="ai-patient-summary-box">
          <div className="ai-summary-header">
            <div className="ai-summary-icon">
              <Sparkle size={18} weight="fill" />
            </div>
            <span className="ai-summary-title">סיכום מטופל AI</span>
          </div>
          <div className="ai-summary-content">
            {patient.patientSummary.split("\n").map((line, index) => {
              // Parse bold text **text**
              const parseBoldText = (text) => {
                const parts = text.split(/(\*\*[^*]+\*\*)/g);
                return parts.map((part, i) => {
                  if (part.startsWith("**") && part.endsWith("**")) {
                    return <strong key={i}>{part.slice(2, -2)}</strong>;
                  }
                  return part;
                });
              };

              // ### Subheading
              if (line.startsWith("### ")) {
                return (
                  <h5 key={index} className="ai-summary-subheading">
                    {parseBoldText(line.replace("### ", ""))}
                  </h5>
                );
              }
              // ## Heading
              if (line.startsWith("## ")) {
                return (
                  <h4 key={index} className="ai-summary-heading">
                    {parseBoldText(line.replace("## ", ""))}
                  </h4>
                );
              }
              // Bullet points
              if (line.startsWith("- ") || line.startsWith("• ")) {
                return (
                  <p key={index} className="ai-summary-bullet">
                    <span className="bullet-icon">•</span>
                    <span>{parseBoldText(line.replace(/^[-•]\s/, ""))}</span>
                  </p>
                );
              }
              // Numbered lists
              if (/^\d+\.\s/.test(line)) {
                const match = line.match(/^(\d+)\.\s(.*)$/);
                return (
                  <p key={index} className="ai-summary-numbered">
                    <span className="number-icon">{match[1]}.</span>
                    <span>{parseBoldText(match[2])}</span>
                  </p>
                );
              }
              // Empty line
              if (line.trim() === "") {
                return <div key={index} className="ai-summary-spacer" />;
              }
              // Regular paragraph
              return (
                <p key={index} className="ai-summary-paragraph">
                  {parseBoldText(line)}
                </p>
              );
            })}
          </div>
        </div>
      )}

      <div className="fields-grid">
        {/* Option toggles (couple / English communication) share one 2-up grid
            in edit mode. English is only offered to clinics that opted in; in
            view mode a short badge is shown only for English patients, so
            Hebrew patients look unchanged. */}
        {isEditing &&
          (clinicManagesCouples ||
            patient.isCouple === true ||
            englishCommunicationEnabled) && (
            <div className="field-group full-width">
              <div className="ptg-grid">
                {(clinicManagesCouples || patient.isCouple === true) && (
                  <CoupleToggle
                    checked={editData?.isCouple === true}
                    onChange={handleCoupleToggleEdit}
                  />
                )}

                {englishCommunicationEnabled && (
                  <EnglishCommunicationToggle
                    language={editData?.communicationLanguage}
                    onChange={(value) =>
                      handleChange("communicationLanguage", value)
                    }
                  />
                )}
              </div>
            </div>
          )}

        {/* Nutrition clinics only — writes the optional patient.nutrition map
            through the existing updatePatient pass-through */}
        {isEditing && isNutritionClinic && (
          <div className="field-group full-width">
            <NutritionSettingsSection
              nutrition={editData?.nutrition}
              patient={patient}
              onChange={(value) => handleChange("nutrition", value)}
            />
          </div>
        )}

        {!isEditing &&
          englishCommunicationEnabled &&
          patient.communicationLanguage === "en" && (
            <div className="field-group full-width">
              <label>שפת תקשורת</label>
              <span className="field-value">אנגלית</span>
            </div>
          )}

        {data.isCouple === true ? (
          isEditing ? (
            <div className="field-group full-width">
              <CouplePartnersFields
                partner1={editData.partner1}
                partner2={editData.partner2}
                smsPrimaryPartner={editData.smsPrimaryPartner}
                errors={coupleErrors}
                onPartnerChange={handlePartnerChange}
                onPrimaryChange={(val) =>
                  handleChange("smsPrimaryPartner", val)
                }
              />
            </div>
          ) : (
            (getCouplePartners(data) || []).map((partner, idx) => (
              <div className="field-group" key={idx}>
                <label>בן/בת זוג {idx + 1}</label>
                <span className="field-value">
                  {`${partner.firstName || ""} ${partner.lastName || ""}`.trim() ||
                    "—"}
                </span>
                <span className="field-value" dir="ltr">
                  {partner.phone || "—"}
                </span>
                {(data.smsPrimaryPartner === 2 ? 2 : 1) === idx + 1 && (
                  <span className="couple-primary-badge">
                    מספר ראשי ל-SMS
                  </span>
                )}
              </div>
            ))
          )
        ) : (
          <>
            <div className="field-group">
              <label>שם פרטי</label>
              {isEditing ? (
                <input
                  type="text"
                  value={data.firstName || ""}
                  onChange={(e) => handleChange("firstName", e.target.value)}
                  dir="rtl"
                />
              ) : (
                <span className="field-value">{data.firstName || "—"}</span>
              )}
            </div>

            <div className="field-group">
              <label>שם משפחה</label>
              {isEditing ? (
                <input
                  type="text"
                  value={data.lastName || ""}
                  onChange={(e) => handleChange("lastName", e.target.value)}
                  dir="rtl"
                />
              ) : (
                <span className="field-value">{data.lastName || "—"}</span>
              )}
            </div>

            <div className="field-group">
              <label>מספר טלפון</label>
              {isEditing ? (
                <input
                  type="tel"
                  value={data.phone || ""}
                  onChange={(e) =>
                    handleChange("phone", formatPhone(e.target.value))
                  }
                  dir="ltr"
                />
              ) : (
                <span className="field-value" dir="ltr">
                  {data.phone || "—"}
                </span>
              )}
            </div>
          </>
        )}

        {/* Nutrition clinics: birth date + gender live on the first tab
            because BMI / BMR / body-fat depend on them */}
        {isNutritionClinic && renderBodyDataFields()}

        <div className="field-group">
          <label>סטטוס</label>
          {isEditing ? (
            <select
              value={data.status || "active"}
              onChange={(e) => handleChange("status", e.target.value)}
            >
              {STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          ) : (
            <span className={`status-badge status-${status.color}`}>
              {status.label}
            </span>
          )}
        </div>

        <div className="field-group full-width">
          <label>סיבת פנייה</label>
          {isEditing ? (
            <textarea
              value={data.referralReason || ""}
              onChange={(e) => handleChange("referralReason", e.target.value)}
              rows={3}
              dir="rtl"
            />
          ) : (
            <span className="field-value">{data.referralReason || "—"}</span>
          )}
        </div>
      </div>
    </div>
  );

  // Birth date + gender. Therapy clinics: on the "פרטים נוספים" tab, as
  // always. Nutrition clinics: on the FIRST tab, each with a one-line hint —
  // BMI / BMR / body-fat silently degrade without them, and the coach had no
  // visible reason why.
  const renderBodyDataFields = () => {
    const missingBirthDate = isNutritionClinic && !data.birthDate;
    const missingGender = isNutritionClinic && !data.gender;
    return (
      <>
        <div className="field-group">
          <label>תאריך לידה</label>
          {isEditing ? (
            <DatePicker
              value={formatDateForInput(data.birthDate)}
              onChange={(e) => handleChange("birthDate", e.target.value)}
            />
          ) : (
            <span className="field-value">{formatDate(data.birthDate)}</span>
          )}
          {isNutritionClinic && (
            <span className={`field-hint ${missingBirthDate ? "missing" : ""}`}>
              {missingBirthDate
                ? "לא הוזן — הגיל נדרש לחישוב BMR/RMR ואחוז שומן"
                : "הגיל משמש לחישוב BMR/RMR ואחוז שומן"}
            </span>
          )}
        </div>

        <div className="field-group">
          <label>מגדר</label>
          {isEditing ? (
            <select
              value={data.gender || ""}
              onChange={(e) => handleChange("gender", e.target.value)}
            >
              {GENDER_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          ) : (
            <span className="field-value">
              {getLabel(data.gender, GENDER_OPTIONS)}
            </span>
          )}
          {isNutritionClinic && (
            <span className={`field-hint ${missingGender ? "missing" : ""}`}>
              {missingGender
                ? "לא הוזן — המגדר נדרש לחישוב BMR/RMR ואחוז שומן"
                : "המגדר משמש לחישוב BMR/RMR ואחוז שומן"}
            </span>
          )}
        </div>
      </>
    );
  };

  // Personal Tab Content
  const renderPersonalTab = () => (
    <div className="profile-section">
      <h3 className="section-title">פרטים אישיים</h3>
      <div className="fields-grid">
        {/* Couple patients: partner ID numbers are edited in the basic tab;
            the top-level idNumber is denormalized from the primary partner */}
        {data.isCouple !== true && (
          <div className="field-group">
            <label>תעודת זהות</label>
            {isEditing ? (
              <input
                type="text"
                value={data.idNumber || ""}
                onChange={(e) => handleChange("idNumber", e.target.value)}
                dir="ltr"
              />
            ) : (
              <span className="field-value" dir="ltr">
                {data.idNumber || "—"}
              </span>
            )}
          </div>
        )}

        {/* Nutrition clinics show these on the first tab instead */}
        {!isNutritionClinic && renderBodyDataFields()}

        <div className="field-group">
          <label>שפות</label>
          {isEditing ? (
            <div className="checkbox-group">
              {LANGUAGE_OPTIONS.map((lang) => (
                <label key={lang} className="checkbox-option">
                  <input
                    type="checkbox"
                    checked={data.languages?.includes(lang) || false}
                    onChange={() => handleLanguageToggle(lang)}
                  />
                  <span>{lang}</span>
                </label>
              ))}
            </div>
          ) : (
            <span className="field-value">
              {data.languages?.join(", ") || "—"}
            </span>
          )}
        </div>
      </div>

      <h3 className="section-title">פרטי התקשרות</h3>
      <div className="fields-grid">
        <div className="field-group">
          <label>אימייל</label>
          {isEditing ? (
            <input
              type="email"
              value={data.email || ""}
              onChange={(e) => handleChange("email", e.target.value)}
              dir="ltr"
            />
          ) : (
            <span className="field-value" dir="ltr">
              {data.email || "—"}
            </span>
          )}
        </div>

        <div className="field-group">
          <label>עיר</label>
          {isEditing ? (
            <input
              type="text"
              value={data.address?.city || ""}
              onChange={(e) =>
                handleNestedChange("address", "city", e.target.value)
              }
              dir="rtl"
            />
          ) : (
            <span className="field-value">{data.address?.city || "—"}</span>
          )}
        </div>

        <div className="field-group">
          <label>רחוב</label>
          {isEditing ? (
            <input
              type="text"
              value={data.address?.street || ""}
              onChange={(e) =>
                handleNestedChange("address", "street", e.target.value)
              }
              dir="rtl"
            />
          ) : (
            <span className="field-value">{data.address?.street || "—"}</span>
          )}
        </div>

        <div className="field-group">
          <label>מספר בית</label>
          {isEditing ? (
            <input
              type="text"
              value={data.address?.number || ""}
              onChange={(e) =>
                handleNestedChange("address", "number", e.target.value)
              }
              dir="rtl"
            />
          ) : (
            <span className="field-value">{data.address?.number || "—"}</span>
          )}
        </div>
      </div>

      <h3 className="section-title">איש קשר לחירום</h3>
      <div className="fields-grid">
        <div className="field-group">
          <label>שם</label>
          {isEditing ? (
            <input
              type="text"
              value={data.emergencyContact?.name || ""}
              onChange={(e) =>
                handleNestedChange("emergencyContact", "name", e.target.value)
              }
            />
          ) : (
            <span className="field-value">
              {data.emergencyContact?.name || "—"}
            </span>
          )}
        </div>

        <div className="field-group">
          <label>קרבה למטופל</label>
          {isEditing ? (
            <input
              type="text"
              value={data.emergencyContact?.relation || ""}
              onChange={(e) =>
                handleNestedChange(
                  "emergencyContact",
                  "relation",
                  e.target.value
                )
              }
            />
          ) : (
            <span className="field-value">
              {data.emergencyContact?.relation || "—"}
            </span>
          )}
        </div>

        <div className="field-group">
          <label>טלפון</label>
          {isEditing ? (
            <input
              type="tel"
              value={data.emergencyContact?.phone || ""}
              onChange={(e) =>
                handleNestedChange(
                  "emergencyContact",
                  "phone",
                  formatPhone(e.target.value)
                )
              }
              dir="ltr"
            />
          ) : (
            <span className="field-value" dir="ltr">
              {data.emergencyContact?.phone || "—"}
            </span>
          )}
        </div>
      </div>

      <h3 className="section-title">הורים / אפוטרופוסים</h3>
      <div className="parents-grid">
        {[
          { key: "parent1", title: "הורה 1" },
          { key: "parent2", title: "הורה 2" },
        ].map(({ key, title }) => (
          <div className="parent-column" key={key}>
            <h4 className="parent-column-title">{title}</h4>

            <div className="field-group">
              <label>שם</label>
              {isEditing ? (
                <input
                  type="text"
                  value={data[key]?.name || ""}
                  onChange={(e) =>
                    handleNestedChange(key, "name", e.target.value)
                  }
                  dir="rtl"
                />
              ) : (
                <span className="field-value">{data[key]?.name || "—"}</span>
              )}
            </div>

            <div className="field-group">
              <label>טלפון</label>
              {isEditing ? (
                <input
                  type="tel"
                  value={data[key]?.phone || ""}
                  onChange={(e) =>
                    handleNestedChange(
                      key,
                      "phone",
                      formatPhone(e.target.value)
                    )
                  }
                  dir="ltr"
                />
              ) : (
                <span className="field-value" dir="ltr">
                  {data[key]?.phone || "—"}
                </span>
              )}
            </div>

            <div className="field-group">
              <label>תעודת זהות</label>
              {isEditing ? (
                <>
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={9}
                    value={data[key]?.id || ""}
                    onChange={(e) =>
                      handleNestedChange(key, "id", formatId(e.target.value))
                    }
                    className={isValidId(data[key]?.id) ? "" : "input-error"}
                    dir="ltr"
                  />
                  {!isValidId(data[key]?.id) && (
                    <span className="field-error">
                      תעודת זהות חייבת להכיל בדיוק 9 ספרות
                    </span>
                  )}
                </>
              ) : (
                <span className="field-value" dir="ltr">
                  {data[key]?.id || "—"}
                </span>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="fields-grid">
        <div className="field-group full-width">
          <label>הערות משפחתיות</label>
          {isEditing ? (
            <textarea
              value={data.familyNotes || ""}
              onChange={(e) => handleChange("familyNotes", e.target.value)}
              rows={2}
              dir="rtl"
            />
          ) : (
            <span className="field-value">{data.familyNotes || "—"}</span>
          )}
        </div>
      </div>
    </div>
  );

  // Clinical Tab Content
  const renderClinicalTab = () => (
    <div className="profile-section">
      <h3 className="section-title">רקע רפואי וקליני</h3>
      <div className="fields-grid">
        <div className="field-group full-width">
          <label>אבחנות קיימות</label>
          {sensitiveHidden ? (
            <span className="field-value field-value-muted">מידע רפואי — אין הרשאה</span>
          ) : isEditing ? (
            <textarea
              value={data.diagnoses || ""}
              onChange={(e) => handleChange("diagnoses", e.target.value)}
              rows={2}
              dir="rtl"
            />
          ) : (
            <span className="field-value">{data.diagnoses || "—"}</span>
          )}
        </div>

        <div className="field-group">
          <label>קופת חולים</label>
          {isEditing ? (
            <select
              value={data.healthFund || ""}
              onChange={(e) => handleChange("healthFund", e.target.value)}
            >
              {HEALTH_FUND_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          ) : (
            <span className="field-value">
              {getLabel(data.healthFund, HEALTH_FUND_OPTIONS)}
            </span>
          )}
        </div>

        <div className="field-group">
          <label>גורם מפנה</label>
          {isEditing ? (
            <input
              type="text"
              value={data.referringDoctor || ""}
              onChange={(e) => handleChange("referringDoctor", e.target.value)}
              dir="rtl"
            />
          ) : (
            <span className="field-value">{data.referringDoctor || "—"}</span>
          )}
        </div>

        <div className="field-group">
          <label>אלרגיות או רגישויות</label>
          {sensitiveHidden ? (
            <span className="field-value field-value-muted">מידע רפואי — אין הרשאה</span>
          ) : isEditing ? (
            <input
              type="text"
              value={data.allergies || ""}
              onChange={(e) => handleChange("allergies", e.target.value)}
              dir="rtl"
            />
          ) : (
            <span className="field-value">{data.allergies || "—"}</span>
          )}
        </div>

        <div className="field-group">
          <label>מסגרת חינוכית</label>
          {isEditing ? (
            <input
              type="text"
              value={data.educationalFramework || ""}
              onChange={(e) =>
                handleChange("educationalFramework", e.target.value)
              }
              dir="rtl"
            />
          ) : (
            <span className="field-value">
              {data.educationalFramework || "—"}
            </span>
          )}
        </div>

        <div className="field-group full-width">
          <label>תרופות קבועות</label>
          {sensitiveHidden ? (
            <span className="field-value field-value-muted">מידע רפואי — אין הרשאה</span>
          ) : isEditing ? (
            <textarea
              value={data.medications || ""}
              onChange={(e) => handleChange("medications", e.target.value)}
              rows={2}
              dir="rtl"
            />
          ) : (
            <span className="field-value">{data.medications || "—"}</span>
          )}
        </div>

        <div className="field-group full-width">
          <label>טיפולים קודמים</label>
          {sensitiveHidden ? (
            <span className="field-value field-value-muted">מידע רפואי — אין הרשאה</span>
          ) : isEditing ? (
            <textarea
              value={data.previousTreatments || ""}
              onChange={(e) =>
                handleChange("previousTreatments", e.target.value)
              }
              rows={2}
              dir="rtl"
            />
          ) : (
            <span className="field-value">
              {data.previousTreatments || "—"}
            </span>
          )}
        </div>

        {isSpeechTherapist && (
        <div className="field-group full-width">
          <label>תוצאות בדיקת שמיעה</label>
          {sensitiveHidden ? (
            <span className="field-value field-value-muted">מידע רפואי — אין הרשאה</span>
          ) : isEditing ? (
            <textarea
              value={data.hearingTestResults || ""}
              onChange={(e) =>
                handleChange("hearingTestResults", e.target.value)
              }
              rows={3}
              dir="rtl"
              placeholder="סוג הבדיקה, תאריך, ממצאים, המלצות..."
            />
          ) : (
            <span className="field-value">
              {data.hearingTestResults || "—"}
            </span>
          )}
        </div>
        )}

        <div className="field-group full-width">
          <label>הערות קליניות כלליות</label>
          {sensitiveHidden ? (
            <span className="field-value field-value-muted">מידע רפואי — אין הרשאה</span>
          ) : isEditing ? (
            <textarea
              value={data.clinicalNotes || ""}
              onChange={(e) => handleChange("clinicalNotes", e.target.value)}
              rows={3}
              dir="rtl"
            />
          ) : (
            <span className="field-value">{data.clinicalNotes || "—"}</span>
          )}
        </div>
      </div>
    </div>
  );

  // Admin Tab Content
  const renderAdminTab = () => (
    <div className="profile-section">
      <h3 className="section-title">ניהול ומנהלה</h3>
      <div className="fields-grid">
        <div className="field-group">
          <label>תאריך הצטרפות</label>
          {isEditing ? (
            <DatePicker
              value={formatDateForInput(data.joinDate)}
              onChange={(e) => handleChange("joinDate", e.target.value)}
            />
          ) : (
            <span className="field-value">{formatDate(data.joinDate)}</span>
          )}
        </div>

        <div className="field-group">
          <label>איך שמע עלינו</label>
          {isEditing ? (
            <ReferralSourceField
              value={data.referralSource || ""}
              onChange={(val) => handleChange("referralSource", val)}
              therapistProfile={therapistProfile}
            />
          ) : (
            <span className="field-value">
              {getReferralSourceLabel(data.referralSource, therapistProfile)}
            </span>
          )}
        </div>

        {patientTypes.length > 0 && (
          <div className="field-group full-width">
            <label>סוג מטופל</label>
            {isEditing ? (
              <div className="patient-types-selector">
                {[...patientTypes]
                  .sort((a, b) => a.order - b.order)
                  .map((type) => {
                    const isSelected = data.patientTypeIds?.includes(type.id);
                    return (
                      <button
                        key={type.id}
                        type="button"
                        className={`patient-type-chip ${
                          isSelected ? "selected" : ""
                        }`}
                        style={{
                          "--type-color": type.color,
                        }}
                        onClick={() => {
                          const currentIds = data.patientTypeIds || [];
                          const newIds = isSelected
                            ? currentIds.filter((id) => id !== type.id)
                            : [...currentIds, type.id];
                          handleChange("patientTypeIds", newIds);
                        }}
                      >
                        <span
                          className="type-color-dot"
                          style={{ backgroundColor: type.color }}
                        />
                        {type.label}
                      </button>
                    );
                  })}
              </div>
            ) : (
              <span className="field-value">
                {data.patientTypeIds?.length > 0 ? (
                  <span className="patient-type-badges">
                    {data.patientTypeIds.map((typeId) => {
                      const type = patientTypes.find((t) => t.id === typeId);
                      if (!type) return null;
                      return (
                        <span
                          key={typeId}
                          className="patient-type-badge"
                          style={{ backgroundColor: type.color }}
                        >
                          {type.label}
                        </span>
                      );
                    })}
                  </span>
                ) : (
                  "—"
                )}
              </span>
            )}
          </div>
        )}

        {treatmentTypes.length > 0 && (
          <div className="field-group">
            <label>סוג טיפול ברירת מחדל</label>
            {isEditing ? (
              <>
                <select
                  value={data.defaultTreatmentTypeId || ""}
                  onChange={(e) => handleChange("defaultTreatmentTypeId", e.target.value)}
                >
                  <option value="">ללא</option>
                  {treatmentTypes.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}{t.rate != null ? ` (₪${t.rate})` : ""}
                    </option>
                  ))}
                </select>
                <span className="field-hint">ימולא אוטומטית בעת קביעת פגישה</span>
              </>
            ) : (
              <span className="field-value">
                {data.defaultTreatmentTypeId
                  ? treatmentTypes.find(t => t.id === data.defaultTreatmentTypeId)?.name || "—"
                  : "—"}
              </span>
            )}
          </div>
        )}

        {canAccessFinance && (
        <div className="field-group">
          <label>תעריף לטיפול (₪)</label>
          {isEditing ? (
            <input
              type="number"
              value={data.treatmentRate || ""}
              onChange={(e) => handleChange("treatmentRate", e.target.value)}
              min="0"
            />
          ) : (
            <span className="field-value">
              {data.treatmentRate ? `₪${data.treatmentRate}` : "—"}
            </span>
          )}
        </div>
        )}

        <div className="field-group">
          <label>משך טיפול (דקות)</label>
          {isEditing ? (
            <input
              type="number"
              value={data.treatmentDuration || ""}
              onChange={(e) =>
                handleChange("treatmentDuration", e.target.value)
              }
              min="1"
              step="1"
            />
          ) : (
            <span className="field-value">
              {data.treatmentDuration ? `${data.treatmentDuration} דק'` : "—"}
            </span>
          )}
        </div>

        <div className="field-group">
          <label>תדירות טיפול</label>
          {isEditing ? (
            <input
              type="text"
              value={data.treatmentFrequency || ""}
              onChange={(e) =>
                handleChange("treatmentFrequency", e.target.value)
              }
            />
          ) : (
            <span className="field-value">
              {data.treatmentFrequency || "—"}
            </span>
          )}
        </div>

        <div className="field-group full-width">
          <label>ימים ושעות מועדפים</label>
          {isEditing ? (
            <DayTimePicker
              value={data.preferredSchedule || {}}
              onChange={(schedule) =>
                handleChange("preferredSchedule", schedule)
              }
            />
          ) : (
            <span className="field-value">
              {formatSchedule(data.preferredSchedule)}
            </span>
          )}
        </div>
      </div>

      <h3 className="section-title">הערות</h3>
      <div className="fields-grid">
        <div className="field-group full-width">
          <label>הערות מנהלתיות</label>
          {isEditing ? (
            <input
              type="text"
              value={data.adminNotes || ""}
              onChange={(e) => handleChange("adminNotes", e.target.value)}
            />
          ) : (
            <span className="field-value">{data.adminNotes || "—"}</span>
          )}
        </div>

        <div className="field-group">
          <label>ויתור סודיות</label>
          {isEditing ? (
            <label className="checkbox-option">
              <input
                type="checkbox"
                checked={data.confidentialityWaiver || false}
                onChange={(e) =>
                  handleChange("confidentialityWaiver", e.target.checked)
                }
              />
              <span>חתום</span>
            </label>
          ) : (
            <span className="field-value">
              {data.confidentialityWaiver ? "חתום" : "לא חתום"}
            </span>
          )}
        </div>
      </div>
    </div>
  );

  const renderEmptyTab = (title) => (
    <div className="empty-tab">
      <HardHat size={64} weight="duotone" />
      <h3>{title}</h3>
      <p>עמוד זה נמצא בבנייה ויהיה זמין בקרוב.</p>
    </div>
  );

  // Helper functions for payments tab
  const formatPaymentDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString("he-IL", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return "—";
    return `₪${amount.toLocaleString("he-IL")}`;
  };

  const getPaymentStatusLabel = (status) => {
    switch (normalizeTransactionStatus(status)) {
      case TRANSACTION_STATUS.PAID:
        return "שולם";
      case TRANSACTION_STATUS.PENDING:
        return "ממתין לתשלום";
      case TRANSACTION_STATUS.EXPECTED:
        return "צפוי";
      case TRANSACTION_STATUS.CANCELLED:
        return "בוטל";
      default:
        return status || "לא ידוע";
    }
  };

  const getPaymentStatusColor = (status) => {
    switch (normalizeTransactionStatus(status)) {
      case TRANSACTION_STATUS.PAID:
        return "green";
      case TRANSACTION_STATUS.PENDING:
        return "orange";
      case TRANSACTION_STATUS.EXPECTED:
        return "blue";
      case TRANSACTION_STATUS.CANCELLED:
        return "gray";
      default:
        return "gray";
    }
  };

  const getPaymentMethodLabel = (method) => {
    switch (method) {
      case "cash":
        return "מזומן";
      case "credit":
        return "אשראי";
      case "transfer":
        return "העברה";
      case "check":
        return "צ׳ק";
      case "bit":
        return "אפליקציה";
      case "punch_card":
        return "כרטיסיה";
      default:
        return method || "—";
    }
  };

  // Calculate patient payment summary
  const paymentSummary = patientTransactions.reduce(
    (acc, tx) => {
      const status = normalizeTransactionStatus(tx.status);
      if (status === TRANSACTION_STATUS.PAID) {
        acc.totalPaid += tx.amount || 0;
        acc.paidCount++;
      } else if (status === TRANSACTION_STATUS.PENDING) {
        acc.totalPending += tx.amount || 0;
        acc.pendingCount++;
      }
      return acc;
    },
    { totalPaid: 0, totalPending: 0, paidCount: 0, pendingCount: 0 }
  );

  // Helper to find treatment linked to an appointment
  const getTreatmentForAppointment = (appointmentId) => {
    if (!appointmentId || !treatments) return null;
    return activeTreatments.find((t) => t.appointmentId === appointmentId);
  };

  // Handle mark as paid for unpaid appointment
  const handleMarkAsPaid = (appointment) => {
    setSelectedTransactionForPayment(null);
    setSelectedAppointmentForPayment(appointment);
    setPaymentAmount(
      appointment.paymentAmount?.toString() ||
        patient?.treatmentRate?.toString() ||
        ""
    );
    setPaymentMethod("cash");
    setShowPaymentModal(true);
  };

  // Handle mark as paid for an existing pending transaction
  const handleMarkTransactionAsPaid = (transaction) => {
    setSelectedAppointmentForPayment(null);
    setSelectedTransactionForPayment(transaction);
    setPaymentAmount(
      transaction.amount?.toString() ||
        patient?.treatmentRate?.toString() ||
        ""
    );
    setPaymentMethod(transaction.paymentMethod || "cash");
    setShowPaymentModal(true);
  };

  // Process the payment
  const handleConfirmPayment = async () => {
    if (
      (!selectedAppointmentForPayment && !selectedTransactionForPayment) ||
      !paymentAmount
    )
      return;

    // Existing pending transaction — update its status to paid
    if (selectedTransactionForPayment) {
      // Grow: active payment link on this transaction? ask before double-collecting
      if (selectedTransactionForPayment.growLinkId) {
        const proceed = await growGuard.ensureNoActiveLink(selectedTransactionForPayment);
        if (!proceed) return;
      }
      setIsProcessingPayment(true);
      try {
        await updateTransaction({
          clinicId,
          transactionId: selectedTransactionForPayment.id,
          updateData: {
            status: TRANSACTION_STATUS.PAID,
            amount: Number(paymentAmount),
            paymentMethod,
            actualPaymentDate: new Date().toISOString(),
          },
          therapistId: currentTherapistId,
          therapistName: currentTherapistName,
          patientName: `${patient.firstName} ${patient.lastName}`,
          patientId,
        }).unwrap();

        const linkedAppointment = selectedTransactionForPayment.appointmentId
          ? appointmentsMap.get(selectedTransactionForPayment.appointmentId)
          : null;

        setShowPaymentModal(false);
        setSelectedTransactionForPayment(null);

        if (isUsingGreenInvoice && linkedAppointment) {
          setInvoiceAppointment({
            ...linkedAppointment,
            actualPaidAmount: Number(paymentAmount),
            paymentMethod,
          });
          setShowInvoiceModal(true);
        }
      } catch (error) {
        console.error("[CK-2211] Error marking transaction as paid:", {
          errorCode: "CK-2211",
          errorMessage: error.message,
          transactionId: selectedTransactionForPayment.id,
          timestamp: new Date().toISOString(),
        });
      } finally {
        setIsProcessingPayment(false);
      }
      return;
    }

    setIsProcessingPayment(true);
    try {
      // Create a transaction for this appointment
      await createTransaction({
        clinicId,
        transactionData: {
          appointmentId: selectedAppointmentForPayment.id,
          patientId,
          patientName: `${patient.firstName} ${patient.lastName}`,
          therapistId: currentTherapistId,
          amount: Number(paymentAmount),
          paymentMethod,
          status: TRANSACTION_STATUS.PAID,
          actualPaymentDate: new Date().toISOString(),
          expectedPaymentDate: selectedAppointmentForPayment.startTime,
          description: `תשלום עבור פגישה מתאריך ${formatAppointmentDateTime(
            selectedAppointmentForPayment.startTime
          )}`,
        },
        therapistId: currentTherapistId,
        therapistName: currentTherapistName,
      }).unwrap();

      setShowPaymentModal(false);
      setSelectedAppointmentForPayment(null);

      if (isUsingGreenInvoice) {
        setInvoiceAppointment({
          ...selectedAppointmentForPayment,
          actualPaidAmount: Number(paymentAmount),
          paymentMethod,
        });
        setShowInvoiceModal(true);
      }
    } catch (error) {
      console.error("[CK-2210] Error marking payment as paid:", {
        errorCode: "CK-2210",
        errorMessage: error.message,
        appointmentId: selectedAppointmentForPayment.id,
        timestamp: new Date().toISOString(),
      });
    } finally {
      setIsProcessingPayment(false);
    }
  };

  // Check if amount differs from treatment rate
  const hasRateMismatch = (amount) => {
    if (!patient?.treatmentRate || !amount) return false;
    return Number(amount) !== Number(patient.treatmentRate);
  };

  // Format appointment date for display
  const formatAppointmentDateTime = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    const dateFormatted = date.toLocaleDateString("he-IL", {
      day: "numeric",
      month: "short",
    });
    const timeFormatted = date.toLocaleTimeString("he-IL", {
      hour: "2-digit",
      minute: "2-digit",
    });
    return `${dateFormatted}, ${timeFormatted}`;
  };

  // Payments Tab Content
  const renderPaymentsTab = () => {
    // One unified chronological ledger: transactions + unpaid completed meetings
    const transactionRows = patientTransactions
      .filter((tx) => {
        const status = normalizeTransactionStatus(tx.status);
        return (
          status === TRANSACTION_STATUS.PAID ||
          status === TRANSACTION_STATUS.PENDING ||
          (status === TRANSACTION_STATUS.CANCELLED && tx.forgivenAmount)
        );
      })
      .map((tx) => {
        const status = normalizeTransactionStatus(tx.status);
        const kind =
          status === TRANSACTION_STATUS.PAID
            ? "paid"
            : status === TRANSACTION_STATUS.PENDING
            ? "pending"
            : "cancelled";
        return {
          key: `tx-${tx.id}`,
          kind,
          sortDate:
            kind === "paid" ? tx.actualPaymentDate : tx.expectedPaymentDate,
          transaction: tx,
        };
      });

    const unpaidRows = unpaidAppointments.map((apt) => ({
      key: `apt-${apt.id}`,
      kind: "unpaid",
      sortDate: apt.startTime,
      appointment: apt,
    }));

    const ledgerRows = [...transactionRows, ...unpaidRows].sort(
      (a, b) => new Date(b.sortDate || 0) - new Date(a.sortDate || 0)
    );

    const filterOptions = [
      { id: "all", label: "הכל", count: ledgerRows.length },
      {
        id: "paid",
        label: "שולם",
        count: transactionRows.filter((r) => r.kind === "paid").length,
      },
      {
        id: "pending",
        label: "ממתין",
        count: transactionRows.filter((r) => r.kind === "pending").length,
      },
      { id: "unpaid", label: "ללא תשלום", count: unpaidRows.length },
    ];

    const visibleRows =
      paymentsFilter === "all"
        ? ledgerRows
        : ledgerRows.filter((row) => row.kind === paymentsFilter);

    const renderTreatmentChip = (appointmentId) => {
      const linkedTreatment = getTreatmentForAppointment(appointmentId);
      if (!linkedTreatment) return null;
      return (
        <button
          className="ppay-chip ppay-chip-link"
          onClick={(e) => {
            e.stopPropagation();
            navigate(
              `/dashboard/patients/${patientId}/treatments/${linkedTreatment.id}`
            );
          }}
        >
          <FileText size={11} weight="fill" />
          <span>סיכום טיפול</span>
        </button>
      );
    };

    return (
      <div className="ppay-tab">
        {/* Compact summary strip */}
        <div className="ppay-summary">
          <div className="ppay-stat paid">
            <span className="ppay-stat-value">
              {formatCurrency(paymentSummary.totalPaid)}
            </span>
            <span className="ppay-stat-label">
              <span className="ppay-stat-dot" />
              שולם · {paymentSummary.paidCount} תשלומים
            </span>
          </div>
          <div className="ppay-stat pending">
            <span className="ppay-stat-value">
              {formatCurrency(paymentSummary.totalPending)}
            </span>
            <span className="ppay-stat-label">
              <span className="ppay-stat-dot" />
              ממתין לתשלום · {paymentSummary.pendingCount}
            </span>
          </div>
          <div className="ppay-stat unpaid">
            <span className="ppay-stat-value">{unpaidAppointments.length}</span>
            <span className="ppay-stat-label">
              <span className="ppay-stat-dot" />
              פגישות ללא תשלום
            </span>
          </div>
        </div>

        {/* Grow: one payment link for the whole open debt + links history */}
        {growLinks.canUseGrow && (
          <GrowPatientLinksHistory
            clinicId={clinicId}
            patientId={patientId}
            patient={patient}
            pendingTransactions={patientTransactions.filter(
              (tx) =>
                normalizeTransactionStatus(tx.status) === TRANSACTION_STATUS.PENDING &&
                !growLinks.linkFor(tx)
            )}
            onOpenLink={growLinks.openLink}
          />
        )}

        {/* Manual payment summary SMS (pending transactions only) */}
        <PaymentSummarySmsCard
          clinicId={clinicId}
          patientId={patientId}
          patient={patient}
          pendingTransactions={patientTransactions.filter(
            (tx) =>
              normalizeTransactionStatus(tx.status) ===
              TRANSACTION_STATUS.PENDING
          )}
          appointments={patientAppointments}
          therapistId={currentTherapistId}
          therapistName={currentTherapistName}
          clinicProfile={clinicProfile}
        />

        {isLoadingTransactions ? (
          <ContentLoader size="sm" compact message="טוען תשלומים..." />
        ) : ledgerRows.length === 0 ? (
          <div className="ppay-state">
            <EmptyDraw variant="payment" />
            <h4>אין תשלומים עדיין</h4>
            <p>
              תשלומים נוצרים אוטומטית כשנקבעת פגישה למטופל. אפשר גם להוסיף
              תשלום ידנית דרך עמוד "פיננסים ותשלומים".
            </p>
          </div>
        ) : (
          <>
            {/* Filter chips */}
            <div className="ppay-filters">
              {filterOptions.map((option) => (
                <button
                  key={option.id}
                  className={`ppay-filter-chip ${
                    paymentsFilter === option.id ? "active" : ""
                  }`}
                  onClick={() => setPaymentsFilter(option.id)}
                >
                  {option.label}
                  {option.count > 0 && (
                    <span className="ppay-filter-count">{option.count}</span>
                  )}
                </button>
              ))}
            </div>

            {/* Unified ledger */}
            <div className="ppay-ledger">
              {visibleRows.length === 0 ? (
                <div className="ppay-filter-empty">אין תשלומים בסינון שנבחר</div>
              ) : (
                visibleRows.map((row) => {
                  if (row.kind === "unpaid") {
                    const apt = row.appointment;
                    const expectedAmount =
                      apt.paymentAmount || patient?.treatmentRate;
                    const treatmentChip = renderTreatmentChip(apt.id);

                    return (
                      <div key={row.key} className="ppay-row">
                        <div className="ppay-row-main">
                          <div className="ppay-row-top">
                            <span className="ppay-pill unpaid">ללא תשלום</span>
                            <span className="ppay-row-date">
                              {formatAppointmentDateTime(apt.startTime)}
                            </span>
                          </div>
                          {treatmentChip && (
                            <div className="ppay-row-chips">{treatmentChip}</div>
                          )}
                        </div>
                        <div className="ppay-row-side">
                          {expectedAmount ? (
                            <span className="ppay-amount muted">
                              צפי {formatCurrency(expectedAmount)}
                            </span>
                          ) : null}
                          <div className="ppay-actions">
                            <button
                              className="ppay-action-btn"
                              onClick={() => handleMarkAsPaid(apt)}
                            >
                              <Check size={14} weight="bold" />
                              סמן כשולם
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  }

                  const tx = row.transaction;
                  const isPaid = row.kind === "paid";
                  const isCancelled = row.kind === "cancelled";
                  const displayDate = isPaid
                    ? tx.actualPaymentDate
                    : tx.expectedPaymentDate;
                  const hasInvoice = transactionHasInvoice(tx);
                  const invoiceInfo = getTransactionInvoiceInfo(tx);
                  const showRateWarning = isPaid && hasRateMismatch(tx.amount);
                  const treatmentChip = renderTreatmentChip(tx.appointmentId);
                  const showChipsRow =
                    treatmentChip ||
                    isUsingGreenInvoice ||
                    showRateWarning ||
                    tx.systemNotes ||
                    (isCancelled && tx.forgivenAmount);

                  return (
                    <div key={row.key} className="ppay-row">
                      <div className="ppay-row-main">
                        <div className="ppay-row-top">
                          <span className={`ppay-pill ${row.kind}`}>
                            {getPaymentStatusLabel(tx.status)}
                          </span>
                          <span className="ppay-row-date">
                            {displayDate
                              ? formatAppointmentDateTime(displayDate)
                              : "—"}
                          </span>
                        </div>
                        {tx.description && (
                          <span className="ppay-row-desc">
                            {tx.description}
                          </span>
                        )}
                        {showChipsRow && (
                          <div className="ppay-row-chips">
                            {treatmentChip}

                            {isUsingGreenInvoice &&
                              (hasInvoice && invoiceInfo?.invoiceUrl ? (
                                <a
                                  href={invoiceInfo.invoiceUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="ppay-chip ppay-chip-invoice"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <Receipt size={11} weight="fill" />
                                  <span>צפה בחשבונית</span>
                                  <ArrowSquareOut size={11} />
                                </a>
                              ) : (
                                <span
                                  className={`ppay-chip ${
                                    hasInvoice
                                      ? "ppay-chip-invoice"
                                      : "ppay-chip-muted"
                                  }`}
                                >
                                  <Receipt size={11} weight="fill" />
                                  <span>
                                    {hasInvoice
                                      ? "הונפקה חשבונית"
                                      : "ללא חשבונית"}
                                  </span>
                                </span>
                              ))}

                            {showRateWarning && (
                              <span className="ppay-chip ppay-chip-warn">
                                <Info size={11} weight="fill" />
                                <span>
                                  שונה מתעריף (
                                  {formatCurrency(patient.treatmentRate)})
                                </span>
                              </span>
                            )}

                            {tx.systemNotes && (
                              <span
                                className="ppay-notes-wrap"
                                aria-label={tx.systemNotes}
                              >
                                <span className="ppay-chip ppay-chip-notes">
                                  <Info size={11} weight="fill" />
                                  <span>הערות מערכת</span>
                                </span>
                                <span className="ppay-notes-tooltip">
                                  {tx.systemNotes}
                                </span>
                              </span>
                            )}

                            {isCancelled && tx.forgivenAmount && (
                              <span className="ppay-chip ppay-chip-forgiven">
                                <span>
                                  תשלום בוטל (
                                  {formatCurrency(tx.forgivenAmount)})
                                </span>
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="ppay-row-side">
                        <div className="ppay-amount-block">
                          <span
                            className={`ppay-amount ${
                              isCancelled ? "cancelled" : ""
                            }`}
                          >
                            {formatCurrency(tx.amount)}
                          </span>
                          {tx.paymentMethod && (
                            <span className="ppay-method">
                              {getPaymentMethodLabel(tx.paymentMethod)}
                            </span>
                          )}
                        </div>
                        {(() => {
                          const growLink = growLinks.linkFor(tx);
                          if (row.kind !== "pending" && !growLink) return null;
                          return (
                            <div className="ppay-actions">
                              {growLink ? (
                                <GrowLinkStatusChip
                                  link={growLink}
                                  size="xs"
                                  onClick={() => growLinks.openLink(growLink)}
                                />
                              ) : (
                                <RequestPaymentButton transactions={[tx]} patient={patient} size="sm" variant="ghost" />
                              )}
                              {row.kind === "pending" && (
                                <button
                                  className="ppay-action-btn"
                                  onClick={() => handleMarkTransactionAsPaid(tx)}
                                >
                                  <Check size={14} weight="bold" />
                                  סמן כשולם
                                </button>
                              )}
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </>
        )}
        {growLinks.popover}
        {growGuard.prompt}
      </div>
    );
  };

  // Helper functions for treatments tab
  const formatTreatmentDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    const dateFormatted = date.toLocaleDateString("he-IL", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
    const timeFormatted = date.toLocaleTimeString("he-IL", {
      hour: "2-digit",
      minute: "2-digit",
    });
    return `${dateFormatted} ${timeFormatted}`;
  };

  const getTreatmentTypeName = (typeId) => {
    if (!typeId) return "פגישת טיפול";
    const type = treatmentTypes.find((t) => t.id === typeId);
    return type?.name || "פגישת טיפול";
  };

  const getTagById = (tagId) => {
    return treatmentTags.find((t) => t.id === tagId);
  };

  const handleNewTreatment = () => {
    navigate(`/dashboard/patients/${patientId}/treatments/new`);
  };

  const handleTreatmentClick = (treatment) => {
    if (treatment?.isDeleted === true) return;
    navigate(`/dashboard/patients/${patientId}/treatments/${treatment.id}`);
  };

  // Render a single field value (handles rich text HTML)
  const renderFieldValue = (field) => {
    if (!field.value && field.value !== false) return <span className="fh-field-empty">—</span>;
    if (field.type === "checkbox") {
      return (
        <span className={`fh-checkbox-value ${field.value === true || field.value === "true" ? "checked" : ""}`}>
          {field.value === true || field.value === "true" ? "✓ כן" : "✗ לא"}
        </span>
      );
    }
    if (field.type === "date") {
      return <span>{field.value}</span>;
    }
    // For long_text and short_text - may contain HTML from RichTextEditor
    if (typeof field.value === "string" && field.value.includes("<")) {
      return <div className="fh-rich-text" dangerouslySetInnerHTML={{ __html: field.value }} />;
    }
    return <span>{field.value}</span>;
  };

  // Plain-text helpers for copy-to-clipboard actions in the Full History view.
  // Headers wrapped as "[ LABEL ]" so they remain bold-feeling without markdown.
  const htmlToPlainText = (html) => {
    if (!html) return "";
    if (typeof html !== "string") return String(html);
    if (!html.includes("<")) return html;
    const div = document.createElement("div");
    div.innerHTML = html;
    div.querySelectorAll("br").forEach((br) => br.replaceWith("\n"));
    div
      .querySelectorAll("p, div, li, h1, h2, h3, h4, h5, h6")
      .forEach((el) => el.append("\n"));
    return (div.textContent || "").replace(/\n{3,}/g, "\n\n").trim();
  };

  const ptHeader = (label) => `[ ${label} ]`;

  const renderFieldValueAsText = (field) => {
    if (field.value === undefined || field.value === null || field.value === "") return "—";
    if (field.type === "checkbox") {
      return field.value === true || field.value === "true" ? "כן" : "לא";
    }
    if (Array.isArray(field.value)) {
      return field.value.length ? field.value.join(", ") : "—";
    }
    if (typeof field.value === "string") {
      return htmlToPlainText(field.value) || "—";
    }
    return String(field.value);
  };

  const formatTreatmentDateForCopy = (date) => {
    const d = new Date(date);
    if (Number.isNaN(d.getTime())) return "";
    const dateStr = d.toLocaleDateString("he-IL", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
    const timeStr = d.toLocaleTimeString("he-IL", {
      hour: "2-digit",
      minute: "2-digit",
    });
    return `${dateStr}, ${timeStr}`;
  };

  const buildAiSummaryText = (treatment) => {
    const dateLabel = formatTreatmentDateForCopy(treatment.treatmentDate);
    const body = htmlToPlainText(treatment.summary) || "(אין סיכום)";
    return `${ptHeader("סיכום AI")} — ${dateLabel}\n\n${body}`;
  };

  const buildTreatmentSummaryText = (treatment) => {
    const blocks = [];
    const dateLabel = formatTreatmentDateForCopy(treatment.treatmentDate);
    blocks.push(`${ptHeader("טיפול")} — ${dateLabel}`);

    if (treatment.title) {
      blocks.push(`${ptHeader("כותרת")}\n${treatment.title}`);
    }

    const typeName = getTreatmentTypeName(treatment.treatmentTypeId);
    if (typeName) {
      blocks.push(`${ptHeader("סוג טיפול")}\n${typeName}`);
    }

    if (treatment.tagIds?.length) {
      const tagNames = treatment.tagIds
        .map((id) => getTagById(id)?.name)
        .filter(Boolean)
        .join(", ");
      if (tagNames) blocks.push(`${ptHeader("תגיות")}\n${tagNames}`);
    }

    if (treatment.fields?.length) {
      const fieldBlocks = treatment.fields.map(
        (f) => `${ptHeader(f.label)}\n${renderFieldValueAsText(f)}`
      );
      blocks.push(`${ptHeader("שדות הטיפול")}\n\n${fieldBlocks.join("\n\n")}`);
    }

    if (
      treatment.summary &&
      treatment.summary !== "מייצר תקציר פגישה..."
    ) {
      blocks.push(`${ptHeader("סיכום AI")}\n${htmlToPlainText(treatment.summary)}`);
    }

    if (isSpeechTherapist && treatment.selectedGameIds?.length) {
      blocks.push(
        `${ptHeader("משחקים / כלים")}\n${treatment.selectedGameIds.join(", ")}`
      );
    }

    if (treatment.comments?.length) {
      const commentBlocks = treatment.comments.map((c) => {
        const cDate = c.createdAt
          ? new Date(c.createdAt).toLocaleDateString("he-IL", {
              day: "numeric",
              month: "short",
              year: "numeric",
            })
          : "";
        const meta = [c.authorName || "מטפל", cDate].filter(Boolean).join(" • ");
        return `${meta}\n${c.text || ""}`;
      });
      blocks.push(`${ptHeader("הערות")}\n${commentBlocks.join("\n\n")}`);
    }

    return blocks.join("\n\n");
  };

  const copyToClipboard = async (text, successMsg, errorCode) => {
    try {
      await navigator.clipboard.writeText(text);
      showSuccess(successMsg);
    } catch (error) {
      console.error(`[${errorCode}] copy to clipboard failed`, {
        errorCode,
        errorMessage: error?.message,
        timestamp: new Date().toISOString(),
      });
      showError("שגיאה בהעתקה ללוח");
    }
  };

  const handleCopyTreatment = (treatment) =>
    copyToClipboard(
      buildTreatmentSummaryText(treatment),
      "סיכום הטיפול הועתק ללוח",
      "CK-2901"
    );

  const handleCopyAiSummary = (treatment) =>
    copyToClipboard(
      buildAiSummaryText(treatment),
      "תקציר ה-AI הועתק ללוח",
      "CK-2902"
    );

  // Full History Modal
  const renderFullHistoryModal = () => {
    if (!showFullHistory) return null;

    // Sort treatments chronologically (oldest first for history reading)
    const sortedTreatments = [...activeTreatments].sort(
      (a, b) => new Date(a.treatmentDate) - new Date(b.treatmentDate)
    );

    return (
      <div className="fh-overlay" onClick={() => setShowFullHistory(false)}>
        <div className="fh-modal" onClick={(e) => e.stopPropagation()}>
          {/* Modal Header */}
          <div className="fh-header">
            <div className="fh-header-info">
              <ClockCounterClockwise size={24} weight="duotone" />
              <div>
                <h2>היסטוריית טיפולים מלאה</h2>
                <span className="fh-count">{activeTreatments.length} טיפולים</span>
              </div>
            </div>
            <div className="fh-header-actions">
              <ModalCloseButton onClick={() => setShowFullHistory(false)} />
            </div>
          </div>

          {/* Modal Body - Scrollable */}
          <div className="fh-body">
            <div className="fh-timeline">
              {sortedTreatments.map((treatment, index) => {
                const treatmentDate = new Date(treatment.treatmentDate);
                const dateStr = treatmentDate.toLocaleDateString("he-IL", {
                  weekday: "long",
                  day: "numeric",
                  month: "long",
                  year: "numeric",
                });
                const timeStr = treatmentDate.toLocaleTimeString("he-IL", {
                  hour: "2-digit",
                  minute: "2-digit",
                });

                return (
                  <div
                    key={treatment.id}
                    className={`fh-treatment expanded ${treatment.status === "draft" ? "draft" : ""}`}
                  >
                    {/* Timeline connector */}
                    <div className="fh-timeline-track">
                      <div className={`fh-timeline-dot ${treatment.status}`}>
                        <span className="fh-treatment-number">{index + 1}</span>
                      </div>
                      {index < sortedTreatments.length - 1 && <div className="fh-timeline-line" />}
                    </div>

                    {/* Treatment Content */}
                    <div className="fh-treatment-content">
                      {/* Header */}
                      <div className="fh-treatment-header">
                        <div className="fh-treatment-meta">
                          <div className="fh-date-time">
                            <CalendarBlank size={15} weight="regular" />
                            <span className="fh-date">{dateStr}</span>
                            <span className="fh-time-separator">|</span>
                            <Clock size={14} weight="regular" />
                            <span className="fh-time">{timeStr}</span>
                          </div>
                          <div className="fh-treatment-badges">
                            <span className={`fh-status-badge ${treatment.status}`}>
                              {treatment.status === "locked" ? (
                                <>
                                  <Lock size={12} weight="bold" />
                                  נעול
                                </>
                              ) : (
                                <>
                                  <LockOpen size={12} weight="bold" />
                                  טיוטה
                                </>
                              )}
                            </span>
                            <span className="fh-type-badge">
                              {getTreatmentTypeName(treatment.treatmentTypeId)}
                            </span>
                          </div>
                        </div>
                        {treatment.title && (
                          <div className="fh-treatment-title-row">
                            <h4 className="fh-treatment-title">{treatment.title}</h4>
                          </div>
                        )}
                      </div>

                      {/* Details - Always visible */}
                      <div className="fh-treatment-details">
                        {/* Tags */}
                        {treatment.tagIds?.length > 0 && (
                          <div className="fh-section fh-tags-section">
                            <div className="fh-tags-list">
                              {treatment.tagIds.map((tagId) => {
                                const tag = getTagById(tagId);
                                return tag ? (
                                  <span
                                    key={tagId}
                                    className="fh-tag"
                                    style={{
                                      backgroundColor: `${tag.color}20`,
                                      color: tag.color,
                                      borderColor: `${tag.color}40`,
                                    }}
                                  >
                                    <Tag size={11} weight="bold" />
                                    {tag.name}
                                  </span>
                                ) : null;
                              })}
                            </div>
                          </div>
                        )}

                        {/* Fields */}
                        {treatment.fields?.length > 0 && (
                          <div className="fh-section fh-fields-section">
                            <h5 className="fh-section-title">
                              <FileText size={16} weight="duotone" />
                              שדות הטיפול
                            </h5>
                            <div className="fh-fields-grid">
                              {treatment.fields.map((field, fIdx) => (
                                <div
                                  key={field.id || fIdx}
                                  className={`fh-field ${field.type === "long_text" ? "full-width" : ""}`}
                                >
                                  <div className="fh-field-label">{field.label}</div>
                                  <div className="fh-field-value">{renderFieldValue(field)}</div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Summary */}
                        {treatment.summary && treatment.summary !== "מייצר תקציר פגישה..." && (
                          <div className="fh-section fh-summary-section">
                            <h5 className="fh-section-title">
                              <Sparkle size={16} weight="duotone" />
                              סיכום AI
                            </h5>
                            <div className="fh-summary-content">
                              {typeof treatment.summary === "string" && treatment.summary.includes("<") ? (
                                <div dangerouslySetInnerHTML={{ __html: treatment.summary }} />
                              ) : (
                                <p>{treatment.summary}</p>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Games - Speech Therapist Only */}
                        {isSpeechTherapist && treatment.selectedGameIds?.length > 0 && (
                          <div className="fh-section fh-games-section">
                            <h5 className="fh-section-title">
                              <GameController size={16} weight="duotone" />
                              משחקים / כלים שנבחרו
                            </h5>
                            <div className="fh-games-list">
                              {treatment.selectedGameIds.map((gameId) => (
                                <span key={gameId} className="fh-game-chip">
                                  <GameController size={13} weight="bold" />
                                  {gameId}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Comments */}
                        {treatment.comments?.length > 0 && (
                          <div className="fh-section fh-comments-section">
                            <h5 className="fh-section-title">
                              <ChatCircleDots size={16} weight="duotone" />
                              הערות ({treatment.comments.length})
                            </h5>
                            <div className="fh-comments-list">
                              {treatment.comments.map((comment, cIdx) => (
                                <div key={cIdx} className="fh-comment">
                                  <div className="fh-comment-header">
                                    <span className="fh-comment-author">{comment.authorName || "מטפל"}</span>
                                    {comment.createdAt && (
                                      <span className="fh-comment-date">
                                        {new Date(comment.createdAt).toLocaleDateString("he-IL", {
                                          day: "numeric",
                                          month: "short",
                                          year: "numeric",
                                        })}
                                      </span>
                                    )}
                                  </div>
                                  {/* addTreatmentComment stores `content`;
                                      this read `comment.text`, so every
                                      treatment comment rendered blank. The
                                      `text` fallback covers any row written
                                      by an older build. */}
                                  <p className="fh-comment-text">
                                    {comment.content || comment.text}
                                  </p>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Footer actions */}
                        <div className="fh-treatment-footer">
                          <button
                            className="fh-copy-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCopyTreatment(treatment);
                            }}
                            title="העתק סיכום טיפול"
                          >
                            <Copy size={16} weight="bold" />
                            העתק סיכום טיפול
                          </button>
                          <button
                            className="fh-copy-btn fh-copy-btn--ai"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCopyAiSummary(treatment);
                            }}
                            disabled={
                              !treatment.summary ||
                              treatment.summary === "מייצר תקציר פגישה..."
                            }
                            title="העתק תקציר AI"
                          >
                            <Copy size={16} weight="bold" />
                            העתק תקציר סיכום טיפול (AI)
                          </button>
                          <button
                            className="fh-open-treatment-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowFullHistory(false);
                              handleTreatmentClick(treatment);
                            }}
                          >
                            <ArrowSquareOut size={16} weight="bold" />
                            פתח טיפול בעמוד מלא
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Treatments Tab Content
  const renderTreatmentsTab = () => {
    if (sensitiveHidden) {
      return (
        <div className="treatments-tab-content">
          <NoAccessState reason="הרשאה נדרשת: גישה למידע רפואי רגיש" />
        </div>
      );
    }
    if (!canViewPatientTreatments) {
      return (
        <div className="treatments-tab-content">
          <NoAccessState reason="קבצים ודוחות טיפול זמינים רק למטפל/ת המשויך/ת" />
        </div>
      );
    }
    return (
    <div className="treatments-tab-content">
      <div className="treatments-header">
        <h3>
          <Clock size={20} weight="duotone" />
          היסטוריית טיפולים
        </h3>
        <div className="treatments-header-actions">
          {activeTreatments.length > 0 && (
            <button
              className="full-history-btn"
              onClick={() => setShowFullHistory(true)}
            >
              <ClockCounterClockwise size={18} weight="bold" />
              תצוגת היסטוריה מלאה
            </button>
          )}
          {canExportData &&
            aiSummaryEnabled &&
            activeTreatments.some((t) => t.status === "locked") && (
              <button
                className="full-history-btn ai-summary-btn"
                onClick={() => navigate(`/dashboard/patients/${patientId}/ai-summary`)}
                title="סיכום של כמה תיעודי טיפול למסמך אחד בעזרת AI"
              >
                <Sparkle size={18} weight="fill" />
                סכם סדרת טיפולים בעזרת AI
              </button>
            )}
          {canEdit && canWriteTreatments !== false && (
            <CustomClinikitActionButton
              icon={<Plus size={16} weight="bold" />}
              onClick={handleNewTreatment}
            >
              תיעוד חדש
            </CustomClinikitActionButton>
          )}
        </div>
      </div>

      {isLoadingTreatments ? (
        <ContentLoader message="טוען טיפולים..." />
      ) : activeTreatments.length === 0 && treatments.length === 0 ? (
        <div className="treatments-empty">
          <EmptyDraw variant="treatment" />
          <h4>עוד אין תיעודי טיפול למטופל הזה</h4>
          <p>
            תעד כל מפגש — סיכום, הערות, תגיות וקבצים מצורפים.
            ככה תוכל לעקוב אחרי ההתקדמות לאורך זמן ולחזור לכל טיפול בקלות.
          </p>
          {canEdit && canWriteTreatments !== false && (
            <CustomClinikitActionButton
              size="large"
              icon={<Plus size={18} weight="bold" />}
              onClick={handleNewTreatment}
            >
              תיעוד טיפול ראשון
            </CustomClinikitActionButton>
          )}
        </div>
      ) : (
        <div className="treatments-timeline">
          {treatments.map((treatment, index) =>
            treatment.isDeleted === true ? (
              /* Soft-deleted report — compact muted marker, not clickable */
              <div
                key={treatment.id}
                className="treatment-card treatment-card--deleted"
                aria-disabled="true"
              >
                <div className="treatment-timeline-dot deleted">
                  <Prohibit size={14} weight="bold" />
                </div>

                {index < treatments.length - 1 && (
                  <div className="treatment-timeline-line" />
                )}

                <div className="treatment-card-content">
                  <div className="treatment-card-header">
                    <div className="treatment-date">
                      <CalendarBlank size={14} weight="regular" />
                      {formatTreatmentDate(treatment.treatmentDate)}
                    </div>
                    <div className="treatment-status deleted">נמחק</div>
                  </div>
                  <div className="treatment-deleted-text">
                    דוח טיפול נמחק – אינו תקף
                    {treatment.deletedReason && (
                      <span className="treatment-deleted-reason">
                        {" "}· {treatment.deletedReason}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ) : (
            <div
              key={treatment.id}
              className={`treatment-card ${
                treatment.status === "draft" ? "draft" : ""
              }`}
              onClick={() => handleTreatmentClick(treatment)}
            >
              <div className="treatment-timeline-dot">
                {treatment.status === "locked" ? (
                  <Lock size={14} weight="bold" />
                ) : (
                  <LockOpen size={14} weight="bold" />
                )}
              </div>

              {index < treatments.length - 1 && (
                <div className="treatment-timeline-line" />
              )}

              <div className="treatment-card-content">
                <div className="treatment-card-header">
                  <div className="treatment-date">
                    <CalendarBlank size={14} weight="regular" />
                    {formatTreatmentDate(treatment.treatmentDate)}
                  </div>
                  <div className="treatment-card-header-end">
                    <div className={`treatment-status ${treatment.status}`}>
                      {treatment.status === "locked" ? "נעול" : "טיוטה"}
                    </div>
                    {treatment.status === "locked" && canExportData && (
                      <button
                        type="button"
                        className="treatment-card-export"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(
                            `/dashboard/patients/${patientId}/treatments/${treatment.id}/export`
                          );
                        }}
                        aria-label="ייצוא PDF"
                        title="ייצוא PDF"
                      >
                        <FilePdf size={15} weight="regular" />
                      </button>
                    )}
                  </div>
                </div>

                <div className="treatment-type">
                  {getTreatmentTypeName(treatment.treatmentTypeId)}
                </div>

                <div className="treatment-summary">
                  {treatment.shortSummary}
                </div>

                {treatment.tagIds?.length > 0 && (
                  <div className="treatment-tags">
                    {treatment.tagIds.map((tagId) => {
                      const tag = getTagById(tagId);
                      return tag ? (
                        <span
                          key={tagId}
                          className="treatment-tag"
                          style={{
                            backgroundColor: `${tag.color}20`,
                            color: tag.color,
                          }}
                        >
                          <Tag size={10} weight="bold" />
                          {tag.name}
                        </span>
                      ) : null;
                    })}
                  </div>
                )}

                {treatment.comments?.length > 0 && (
                  <div className="treatment-comments-count">
                    {treatment.comments.length} הערות
                  </div>
                )}
              </div>
            </div>
            )
          )}
        </div>
      )}
    </div>
    );
  };

  const renderMainTabContent = () => {
    switch (activeMainTab) {
      case "profile":
        return (
          <div className="profile-tab-content pp-profile">
            <div className={`pp-sections${isEditing ? "" : " pp-sections--view"}`}>
              {/* Multi-therapist: assigned therapists + all future appointments */}
              {allowMultipleTherapists && hasMultipleTherapists && (
                <section className="pp-section multi-therapist-card">
                  <h2 className="pp-section-title">מטפלים משויכים</h2>
                  <div className="pp-section-body">
                    <div className="multi-therapist-chips">
                      {patientTherapistIds.map((tid) => (
                        <span key={tid} className={`therapist-chip${tid === currentTherapistId ? " therapist-chip-me" : ""}`}>
                          {getTherapistNameById(tid) || tid}
                          {tid === currentTherapistId && " (אני)"}
                        </span>
                      ))}
                    </div>
                    {patientAllFutureAppointments.length > 0 && (
                      <div className="multi-therapist-appointments-section">
                        <div className="multi-appt-section-header">
                          <CalendarBlank size={16} weight="duotone" />
                          <span>פגישות עתידיות ({patientAllFutureAppointments.length})</span>
                        </div>
                        <div className="multi-appt-table">
                          {patientAllFutureAppointments.map((apt) => (
                            <div key={apt.id} className="multi-appt-row">
                              <span className="multi-appt-date">
                                {new Date(apt.startTime).toLocaleDateString("he-IL", { day: "numeric", month: "short" })}
                              </span>
                              <span className="multi-appt-time">
                                {new Date(apt.startTime).toLocaleTimeString("he-IL", { hour: "2-digit", minute: "2-digit" })}
                              </span>
                              <span className="multi-appt-therapist">{apt.therapistName || getTherapistNameById(apt.therapistId)}</span>
                              {apt.treatmentTypeName && (
                                <span className="multi-appt-type">{apt.treatmentTypeName}</span>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </section>
              )}

              {PROFILE_TABS.map((tab) => (
                <section
                  key={tab.id}
                  className={`pp-section pp-section--${tab.id}`}
                >
                  <h2 className="pp-section-title">{tab.label}</h2>
                  <div className="pp-section-body">
                    {tab.id === "basic" && renderBasicTab()}
                    {tab.id === "personal" && renderPersonalTab()}
                    {tab.id === "clinical" && renderClinicalTab()}
                    {tab.id === "admin" && renderAdminTab()}
                  </div>
                </section>
              ))}
            </div>

            {/* Sticky save bar — the form is one long scroll now, so the
                primary action must never be off-screen while editing. */}
            {canEdit && isEditing && (
              <div className="pp-editbar">
                <span className="pp-editbar-hint">
                  {isFormDirty ? "יש שינויים שלא נשמרו" : "מצב עריכה"}
                </span>
                <div className="pp-editbar-actions">
                  <button className="cancel-btn" onClick={handleCancelEdit}>
                    <X size={16} weight="bold" />
                    <span>ביטול</span>
                  </button>
                  <button
                    className="patient-edit-save-button-98765"
                    onClick={handleSave}
                    disabled={isUpdating || isRegeneratingSummary}
                  >
                    {isRegeneratingSummary ? (
                      <LoaderLoop size="xs" />
                    ) : (
                      <FloppyDisk size={16} weight="bold" />
                    )}
                    <span>
                      {isRegeneratingSummary
                        ? "מעדכן סיכום AI..."
                        : isUpdating
                        ? "שומר..."
                        : "שמור"}
                    </span>
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      case "treatments":
        return renderTreatmentsTab();
      case "appointmentsDiary":
        return (
          <PatientAppointmentsDiaryTab
            patient={patient}
            patientId={patientId}
            canEdit={canEdit}
            treatments={activeTreatments}
            transactions={patientTransactions}
            clinicTherapists={clinicTherapists}
            treatmentTypes={treatmentTypes}
            onMarkAsPaid={handleMarkAsPaid}
            canViewAppointments={canViewPatientAppointments}
          />
        );
      case "files":
        if (!canViewPatientFiles) {
          return (
            <NoAccessState reason="קבצים זמינים רק למטפל/ת המשויך/ת" />
          );
        }
        return <PatientFilesTab patientId={patientId} canEdit={canEdit} />;
      case "formsAndQuestionnaires":
        return renderFormsAndQuestionnairesTab();
      case "payments":
        return renderPaymentsTab();
      case "punchCard":
        return (
          <PunchCardTab
            patientId={patientId}
            patient={patient}
            canEdit={canEdit}
          />
        );
      default:
        return null;
    }
  };

  // Open a signed-form PDF — always fetches a fresh download-token URL so
  // historical broken pdfUrls heal themselves on click.
  const handleOpenSignedFormPdf = async (form) => {
    if (!clinicId || !form?.id) return;
    setRegeneratingPdfId(form.id);
    // Open a tab synchronously so the popup blocker doesn't intervene
    const tab = window.open("", "_blank");
    try {
      const result = await regenerateSignedFormPdf(clinicId, form.id);
      if (result?.pdfUrl) {
        if (tab) tab.location.href = result.pdfUrl;
        else window.open(result.pdfUrl, "_blank");
      } else if (form.pdfUrl) {
        if (tab) tab.location.href = form.pdfUrl;
      } else if (tab) {
        tab.close();
        showError("לא ניתן להפיק את ה-PDF");
      }
    } catch (error) {
      console.error("[CK-2251] Error opening signed form PDF:", {
        errorCode: "CK-2251",
        errorMessage: error.message,
        clinicId,
        signedFormId: form.id,
        timestamp: new Date().toISOString(),
      });
      if (tab) tab.close();
      showError("שגיאה בפתיחת ה-PDF");
    } finally {
      setRegeneratingPdfId(null);
    }
  };

  // Export a completed questionnaire to PDF. First click generates+caches on the
  // server; subsequent clicks reuse the cached URL.
  const handleExportQuestionnairePdf = async (submission) => {
    if (!clinicId || !submission?.id) return;
    setExportingQuestionnairePdfId(submission.id);
    // Open a tab synchronously so the popup blocker doesn't intervene
    const tab = window.open("", "_blank");
    try {
      const result = await generateQuestionnairePDF(clinicId, submission.id);
      if (result?.pdfUrl) {
        if (tab) tab.location.href = result.pdfUrl;
        else window.open(result.pdfUrl, "_blank", "noopener,noreferrer");
      } else if (tab) {
        tab.close();
        showError("לא ניתן להפיק את ה-PDF");
      }
    } catch (error) {
      console.error("[CK-2252] Error exporting questionnaire PDF:", {
        errorCode: "CK-2252",
        errorMessage: error.message,
        clinicId,
        linkId: submission.id,
        timestamp: new Date().toISOString(),
      });
      if (tab) tab.close();
      showError("שגיאה בייצוא ה-PDF");
    } finally {
      setExportingQuestionnairePdfId(null);
    }
  };

  const handleResendQuestionnaire = async (submission) => {
    try {
      setResendingQuestionnaireId(submission.id);
      await resendQuestionnaire({
        clinicId,
        linkId: submission.id,
      }).unwrap();
      showSuccess("השאלון נשלח שוב בהצלחה");
    } catch (error) {
      console.error("[CK-2250] Error resending questionnaire:", {
        errorCode: "CK-2250",
        errorMessage: error.message,
        clinicId,
        linkId: submission.id,
        timestamp: new Date().toISOString(),
      });
      showError("שגיאה בשליחת השאלון מחדש");
    } finally {
      setResendingQuestionnaireId(null);
    }
  };

  // Render forms and questionnaires tab
  const renderFormsAndQuestionnairesTab = () => {
    // signedForms / questionnaireResponses reads require clinical.viewSensitive
    if (sensitiveHidden) {
      return <NoAccessState reason="הרשאה נדרשת: גישה למידע רפואי רגיש" />;
    }
    const isLoading = isLoadingSignedForms || isLoadingQuestionnaireSubmissions;
    const hasNoContent = signedForms.length === 0 && questionnaireSubmissions.length === 0;

    if (isLoading) {
      return (
        <ContentLoader size="sm" compact message="טוען טפסים ושאלונים..." />
      );
    }

    if (hasNoContent) {
      return (
        <div className="signed-forms-empty">
          <EmptyDraw variant="form" />
          <h3>עוד לא נשלחו טפסים או שאלונים למטופל הזה</h3>
          <p className="signed-forms-empty-subtitle">
            ברגע שתשלח טופס או שאלון, הם יופיעו כאן אוטומטית עם כל התשובות והחתימות.
          </p>

          <div className="signed-forms-empty-guides">
            <div className="signed-forms-guide-card">
              <div className="signed-forms-guide-icon">
                <FileText size={22} weight="duotone" />
              </div>
              <div className="signed-forms-guide-text">
                <strong>טפסים</strong>
                <span>
                  היכנס ל<Toolbox size={14} weight="bold" className="inline-icon" /> ארגז הכלים, צור טופס פעם אחת (הסכם טיפול, הצהרת בריאות וכו׳) ושלח אותו לכל מטופל שצריך.
                  המטופל חותם דיגיטלית — והטופס החתום מתווסף לכאן.
                </span>
              </div>
            </div>

            <div className="signed-forms-guide-card">
              <div className="signed-forms-guide-icon questionnaire">
                <ListChecks size={22} weight="duotone" />
              </div>
              <div className="signed-forms-guide-text">
                <strong>שאלונים</strong>
                <span>
                  צור שאלון מותאם אישית ב<Toolbox size={14} weight="bold" className="inline-icon" /> ארגז הכלים ושלח אותו למטופל בלחיצה.
                  המטופל ממלא מהנייד — והתשובות מופיעות כאן מסודרות ומוכנות לצפייה.
                </span>
              </div>
            </div>
          </div>

          <div className="signed-forms-empty-tip">
            <PaperPlaneTilt size={16} weight="fill" />
            <span>ניתן לשלוח את אותו טופס או שאלון שוב ושוב — בלי ליצור מחדש</span>
          </div>
        </div>
      );
    }

    return (
      <div className="signed-forms-tab-content">
        {/* Signed Forms Section */}
        {signedForms.length > 0 && (
          <section className="pp-section">
            <h2 className="pp-section-title">
              טפסים חתומים
              <span className="pp-count">{signedForms.length}</span>
            </h2>
            <div className="signed-forms-list">
              {signedForms.map((form) => (
                <div key={form.id} className="signed-form-card">
                  <div className="signed-form-icon">
                    <CheckCircle size={20} weight="fill" />
                  </div>
                  <div className="signed-form-content">
                    <h4 className="signed-form-name">{form.formName}</h4>
                    <div className="signed-form-meta">
                      <span className="signed-form-date">
                        נחתם:{" "}
                        {new Date(form.signedAt).toLocaleDateString("he-IL", {
                          day: "numeric",
                          month: "short",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </span>
                      <span className="signed-form-type">
                        {form.signatureType === "drawn" ? "חתימה ביד" : "הקלדת שם"}
                      </span>
                    </div>
                  </div>
                  <div className="signed-form-actions">
                    <button
                      type="button"
                      className="view-pdf-btn"
                      title="ייצוא ל-PDF"
                      onClick={() => handleOpenSignedFormPdf(form)}
                      disabled={regeneratingPdfId === form.id}
                    >
                      {regeneratingPdfId === form.id ? (
                        <LoaderLoop size="xs" />
                      ) : (
                        <ArrowSquareOut size={18} weight="regular" />
                      )}
                      <span>
                        {regeneratingPdfId === form.id ? "מכין PDF..." : "ייצוא ל-PDF"}
                      </span>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Questionnaire Submissions Section */}
        {questionnaireSubmissions.length > 0 && (
          <section className="pp-section">
            <h2 className="pp-section-title">
              שאלונים
              <span className="pp-count">{questionnaireSubmissions.length}</span>
            </h2>
            <div className="signed-forms-list questionnaire-submissions-list">
              {questionnaireSubmissions.map((submission) => (
                <div key={submission.id} className={`signed-form-card questionnaire-submission ${submission.status}`}>
                  <div className={`signed-form-icon questionnaire-icon ${submission.status}`}>
                    {submission.status === "completed" ? (
                      <CheckCircle size={20} weight="fill" />
                    ) : submission.status === "viewed" ? (
                      <Eye size={20} weight="fill" />
                    ) : (
                      <Clock size={20} weight="fill" />
                    )}
                  </div>
                  <div className="signed-form-content">
                    <h4 className="signed-form-name">{submission.questionnaireName}</h4>
                    <div className="signed-form-meta">
                      <span className={`status-badge ${submission.status}`}>
                        {submission.status === "completed" ? "הושלם" : submission.status === "viewed" ? "נצפה" : "ממתין"}
                      </span>
                      <span className="signed-form-date">
                        נשלח:{" "}
                        {new Date(submission.createdAt).toLocaleDateString("he-IL", {
                          day: "numeric",
                          month: "short",
                          year: "numeric",
                        })}
                      </span>
                      {submission.completedAt && (
                        <span className="signed-form-date">
                          הושלם:{" "}
                          {new Date(submission.completedAt).toLocaleDateString("he-IL", {
                            day: "numeric",
                            month: "short",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </span>
                      )}
                    </div>
                    <div className="questionnaire-status-text">
                      {submission.status === "completed"
                        ? submission.signature?.data
                          ? "המטופל ענה על השאלון וחתם דיגיטלית"
                          : "המטופל ענה על השאלון"
                        : submission.requireSignature === true
                          ? "ממתין למענה ולחתימה של המטופל"
                          : "ממתין למענה של המטופל"}
                    </div>
                  </div>
                  <div className="questionnaire-actions">
                    {submission.status === "completed" && submission.fields && (
                      <>
                        <button
                          className="questionnaire-view-btn"
                          onClick={() => setViewingQuestionnaire(submission)}
                          title="הצגת שאלון"
                        >
                          <Eye size={16} weight="regular" />
                          הצגת שאלון
                        </button>
                        <button
                          className="questionnaire-export-pdf-btn"
                          onClick={() => handleExportQuestionnairePdf(submission)}
                          disabled={exportingQuestionnairePdfId === submission.id}
                          title="ייצוא ל-PDF"
                        >
                          {exportingQuestionnairePdfId === submission.id ? (
                            <LoaderLoop size="xs" />
                          ) : (
                            <FilePdf size={16} weight="regular" />
                          )}
                          {exportingQuestionnairePdfId === submission.id
                            ? "מכין PDF..."
                            : "ייצוא ל-PDF"}
                        </button>
                      </>
                    )}
                    {submission.status !== "completed" && (
                      <button
                        className="questionnaire-resend-btn"
                        onClick={() => handleResendQuestionnaire(submission)}
                        disabled={resendingQuestionnaireId === submission.id}
                        title="שלח שוב"
                      >
                        {resendingQuestionnaireId === submission.id ? (
                          <LoaderLoop size="xs" />
                        ) : (
                          <ArrowsClockwise size={16} weight="regular" />
                        )}
                        שלח שוב
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
    );
  };

  return (
    <div className="patient-profile-page">
      {/* Cancel Edit Warning Dialog - shown when clicking cancel button while editing */}
      <ConfirmDialog
        isOpen={showCancelWarning}
        onClose={() => setShowCancelWarning(false)}
        onConfirm={handleConfirmCancelEdit}
        title="יש שינויים שלא נשמרו"
        message="יש שינויים שלא נשמרו - האם אתה בטוח שברצונך לצאת?"
        confirmText="כן, צא מהעמוד"
        cancelText="לא, חזור לעריכה"
        type="warning"
        icon="warning"
      />

      {/* Deactivate / Reactivate Patient Dialog */}
      <ConfirmDialog
        isOpen={showInactivityConfirm}
        onClose={() => setShowInactivityConfirm(false)}
        onConfirm={handleConfirmToggleActive}
        title={
          !isDeactivating
            ? "הפעלת מטופל מחדש"
            : deactivationBlocked
              ? "לא ניתן להשבית את המטופל"
              : "השבתת מטופל"
        }
        message={
          !isDeactivating ? (
            "המטופל יחזור לרשימת המטופלים הפעילים ויופיע בכל המקומות (יומן, גבייה, דוחות)."
          ) : isFetchingInactivityAppointments ? (
            "בודק תורים עתידיים של המטופל..."
          ) : isInactivityAppointmentsError ? (
            "לא ניתן לבדוק את התורים העתידיים של המטופל כרגע. נסה שוב מאוחר יותר."
          ) : deactivationBlocked ? (
            <>
              {`למטופל יש ${futureAppointmentsForInactivity.length} ` +
                `${futureAppointmentsForInactivity.length === 1 ? "תור עתידי" : "תורים עתידיים"} ביומן` +
                `${
                  futureAppointmentsForInactivity[0]?.startTime
                    ? ` (הקרוב ביותר: ${formatHebrewDate(
                        futureAppointmentsForInactivity[0].startTime
                      )} בשעה ${formatTime(
                        futureAppointmentsForInactivity[0].startTime
                      )})`
                    : ""
                }. יש לבטל את התורים העתידיים ביומן לפני השבתת המטופל.`}
              {pendingPaymentsForInactivity.count > 0 && (
                <>
                  <br />
                  <br />
                  {`כמו כן, קיימים ${pendingPaymentsForInactivity.count} תשלומים הממתינים לתשלום בסך ${formatCurrency(
                    pendingPaymentsForInactivity.total
                  )}.`}
                </>
              )}
            </>
          ) : (
            <>
              המטופל יוסתר מהרשימות, מהיומן הצידי, מטפסי גבייה ומדוחות. הנתונים
              נשמרים — ניתן להפעיל את המטופל מחדש בכל עת.
              {pendingPaymentsForInactivity.count > 0 && (
                <>
                  <br />
                  <br />
                  {`שים לב: למטופל ${pendingPaymentsForInactivity.count} תשלומים הממתינים לתשלום בסך ${formatCurrency(
                    pendingPaymentsForInactivity.total
                  )}. החובות יישמרו, אך המטופל לא יופיע יותר במסכי הגבייה ובדוחות.`}
                </>
              )}
            </>
          )
        }
        confirmText={!isDeactivating ? "הפעל מחדש" : "השבת מטופל"}
        cancelText={deactivationBlocked ? "סגור" : "ביטול"}
        type={
          !isDeactivating ? "info" : deactivationBlocked ? "danger" : "warning"
        }
        icon="warning"
        hideConfirm={deactivationBlocked}
        confirmDisabled={deactivationCheckPending || isTogglingActive}
      />

      {/* Full History Modal */}
      {renderFullHistoryModal()}

      {/* Reassign Patient Modal */}
      {showReassignModal && (
        <div className="modal-overlay">
          <div className="reassign-modal">
            <ModalCloseButton onClick={() => setShowReassignModal(false)} />

            {allowMultipleTherapists ? (
              <>
                <div className="reassign-multi-header">
                  <div className="reassign-multi-icon">
                    <UsersThree size={20} weight="fill" />
                  </div>
                  <div className="reassign-multi-header-text">
                    <h3>שיוך למטפלים</h3>
                    <span className="reassign-multi-patient-badge">
                      {patient.firstName} {patient.lastName}
                    </span>
                  </div>
                </div>

                <div className="reassign-multi-body">
                  <label className="reassign-multi-label">בחר מטפלים לשיוך</label>
                  <div className="multi-therapist-checkbox-list">
                    {activeTherapists.map((t) => {
                      const isChecked = multiTherapistSelection.includes(t.uid);
                      const isMe = t.uid === currentTherapistId;
                      return (
                        <div
                          key={t.uid}
                          className={`multi-therapist-checkbox-item${isChecked ? " checked" : ""}`}
                          onClick={() => toggleTherapistSelection(t.uid)}
                        >
                          <div className={`mt-checkbox-box${isChecked ? " mt-checkbox-checked" : ""}`}>
                            {isChecked && <Check size={13} weight="bold" />}
                          </div>
                          <div className="mt-checkbox-info">
                            <span className="mt-checkbox-name">
                              {t.firstName} {t.lastName}
                            </span>
                            {isMe && <span className="mt-checkbox-me">אני</span>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {multiTherapistSelection.length === 0 && (
                    <p className="multi-therapist-min-warning">
                      <WarningCircle size={14} weight="fill" />
                      יש לבחור לפחות מטפל אחד
                    </p>
                  )}
                </div>

                <div className="reassign-multi-footer">
                  <button
                    className="reassign-multi-btn reassign-multi-btn-cancel"
                    onClick={() => setShowReassignModal(false)}
                    disabled={isReassigning}
                  >
                    ביטול
                  </button>
                  <button
                    className="reassign-multi-btn reassign-multi-btn-confirm"
                    onClick={handleConfirmMultiTherapist}
                    disabled={multiTherapistSelection.length === 0 || isReassigning}
                  >
                    {isReassigning ? "מעדכן..." : "עדכן שיוך"}
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="modal-header">
                  <ArrowsClockwise size={22} weight="bold" className="reassign-icon" />
                  <h3>שיוך מטופל/ת למטפל/ת אחר/ת</h3>
                </div>

                <div className="reassign-modal-body">
                  <p className="reassign-patient-name">
                    {patient.firstName} {patient.lastName}
                  </p>

                  {patient.therapistId && (
                    <div className="reassign-current">
                      <span className="label">מטפל/ת נוכחי/ת:</span>
                      <span className="value">{getTherapistNameById(patient.therapistId)}</span>
                    </div>
                  )}

                  <div className="form-group">
                    <label>בחר מטפל/ת חדש/ה</label>
                    <select
                      value={reassignTargetId}
                      onChange={(e) => setReassignTargetId(e.target.value)}
                      className="therapist-select"
                    >
                      <option value="">— בחר מטפל/ת —</option>
                      {activeTherapists
                        .filter((t) => t.uid !== patient.therapistId)
                        .map((t) => (
                          <option key={t.uid} value={t.uid}>
                            {t.firstName} {t.lastName}
                            {t.uid === currentTherapistId ? " (אני)" : ""}
                          </option>
                        ))}
                    </select>
                  </div>

                  {reassignTargetId && hasFutureAppointments && (
                    <div className="reassign-appointments-question">
                      <label>למטופל/ת {patientFutureAppointments.length} תורים עתידיים. מה לעשות איתם?</label>
                      <div className="reassign-options">
                        <label className="reassign-option">
                          <input
                            type="radio"
                            name="transferAppointments"
                            checked={reassignTransferAppointments === true}
                            onChange={() => setReassignTransferAppointments(true)}
                          />
                          <span>להעביר תורים עתידיים למטפל/ת החדש/ה</span>
                        </label>
                        <label className="reassign-option">
                          <input
                            type="radio"
                            name="transferAppointments"
                            checked={reassignTransferAppointments === false}
                            onChange={() => setReassignTransferAppointments(false)}
                          />
                          <span>בטל את התורים העתידיים של מטופל/ת זה/ו</span>
                        </label>
                      </div>
                    </div>
                  )}
                </div>

                <div className="modal-actions">
                  <button
                    className="btn-secondary"
                    onClick={() => setShowReassignModal(false)}
                    disabled={isReassigning}
                  >
                    ביטול
                  </button>
                  <button
                    className="btn-primary"
                    onClick={handleConfirmReassign}
                    disabled={!reassignTargetId || (hasFutureAppointments && reassignTransferAppointments === null) || isReassigning}
                  >
                    {isReassigning ? "משייך..." : "שייך מטופל/ת"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <div className="pp-topbar">
        <button className="back-btn" onClick={handleBack}>
          <ArrowRight size={16} weight="bold" />
          <span>חזרה לרשימת המטופלים</span>
        </button>
      </div>

      {/* ===== Hero: identity + facts + tabs — visible on EVERY tab ===== */}
      <div className={`pp-hero${patient.isActive === false ? " pp-hero--inactive" : ""}`}>
        <div className="pp-identity">
          <div className="pp-avatar" aria-hidden="true">
            {patient.firstName?.charAt(0)}
            {patient.lastName?.charAt(0)}
          </div>

          <div className="pp-identity-main">
            <div className="pp-name-row">
              <h1 className="pp-name">
                {patient.firstName} {patient.lastName}
              </h1>
              <div className="pp-badges patient-main-badges">
                {patient.isActive === false ? (
                  <span className="status-badge status-gray status-inactive">
                    לא פעיל
                  </span>
                ) : (
                  <span className={`status-badge status-${status.color}`}>
                    {status.label}
                  </span>
                )}
                {!isOwnPatient && sharesAllPatients && (
                  <span
                    className="status-badge status-blue shared-patient-badge"
                    title="מצב שיתוף מטופלים: כל הצוות"
                  >
                    <UsersThree size={12} weight="bold" />
                    משותף/ת לכל הצוות
                  </span>
                )}
                {isReadOnly && !isAdmin && (
                  <span className="status-badge status-gray view-only-badge">
                    <Eye size={12} weight="bold" />
                    צפייה בלבד
                  </span>
                )}
              </div>
            </div>

            {/* One meta line: the facts a therapist needs before clicking anything */}
            <div className="pp-meta">
              {patient.phone && (
                <span className="pp-meta-item" dir="ltr">
                  <Phone size={13} weight="regular" />
                  {patient.phone}
                </span>
              )}
              {canReassign && !isOwnPatient && patient?.therapistId && !hasMultipleTherapists ? (
                <span className="pp-meta-item">
                  <User size={13} weight="regular" />
                  מטופל/ת של <strong>{getTherapistNameById(patient.therapistId)}</strong>
                </span>
              ) : (
                !isPrimaryTherapist && primaryTherapistName && (
                  <span className="pp-meta-item">
                    <User size={13} weight="regular" />
                    מטפל/ת ראשי/ת <strong>{primaryTherapistName}</strong>
                  </span>
                )
              )}
            </div>

            {/* Nutrition clinics: the client's personal-page link stays
                always visible, no menu dive */}
            {isNutritionClinic && (
              <div className="profile-personal-link">
                <PersonalPageLink clinicId={clinicId} patient={patient} variant="row" />
              </div>
            )}
          </div>

          <div className="pp-actions profile-header-actions">
            <HelpButton featureKey="patient_profile" />
            {canEdit && !isEditing && (
              <button className="pp-btn pp-btn--primary edit-btn" onClick={handleEditClick}>
                <PencilSimple size={16} weight="bold" />
                <span>עריכה</span>
              </button>
            )}
            {canEdit && isEditing && (
              <div className="edit-actions">
                <button className="cancel-btn" onClick={handleCancelEdit}>
                  <X size={16} weight="bold" />
                  <span>ביטול</span>
                </button>
                <button
                  className="patient-edit-save-button-98765"
                  onClick={handleSave}
                  disabled={isUpdating || isRegeneratingSummary}
                >
                  {isRegeneratingSummary ? (
                    <LoaderLoop size="xs" />
                  ) : (
                    <FloppyDisk size={16} weight="bold" />
                  )}
                  <span>
                    {isRegeneratingSummary
                      ? "מעדכן סיכום AI..."
                      : isUpdating
                      ? "שומר..."
                      : "שמור"}
                  </span>
                </button>
              </div>
            )}
            {!isEditing && (
              <div className="pp-more" ref={moreMenuRef}>
                <button
                  type="button"
                  className="pp-btn pp-btn--icon"
                  aria-label="פעולות נוספות"
                  aria-haspopup="menu"
                  aria-expanded={showMoreMenu}
                  onClick={() => setShowMoreMenu((v) => !v)}
                >
                  <DotsThree size={20} weight="bold" />
                </button>
                {showMoreMenu && (
                  <div className="pp-more-menu" role="menu">
                    {canReassign && (
                      <button
                        type="button"
                        role="menuitem"
                        className="pp-more-item"
                        onClick={() => {
                          setShowMoreMenu(false);
                          handleOpenReassign();
                        }}
                      >
                        <ArrowsClockwise size={16} weight="bold" />
                        <span>שיוך למטפל/ת</span>
                      </button>
                    )}
                    {canEdit && (
                      <button
                        type="button"
                        role="menuitem"
                        className={`pp-more-item${patient.isActive === false ? "" : " pp-more-item--muted"}`}
                        disabled={isTogglingActive}
                        onClick={() => {
                          setShowMoreMenu(false);
                          handleToggleActiveClick();
                        }}
                      >
                        {patient.isActive === false ? (
                          <>
                            <ArrowUUpLeft size={16} weight="bold" />
                            <span>הפעל/י מטופל/ת מחדש</span>
                          </>
                        ) : (
                          <>
                            <Archive size={16} weight="bold" />
                            <span>השבת/י מטופל/ת</span>
                          </>
                        )}
                      </button>
                    )}
                    {patientId && (
                      <button
                        type="button"
                        role="menuitem"
                        className="pp-more-item"
                        onClick={() => {
                          handleCopyPatientId();
                          setShowMoreMenu(false);
                        }}
                      >
                        {copiedId ? <Check size={16} weight="bold" /> : <Copy size={16} weight="bold" />}
                        <span>{copiedId ? "המזהה הועתק" : "העתקת מזהה מטופל/ת"}</span>
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Main tabs — flat strip on desktop, native select on mobile */}
        <TabBar
          className="pp-tabs main-tabs-nav"
          tabClassName="main-tab-btn"
          ariaLabel="מעבר בין חלקי כרטיס המטופל"
          activeId={activeMainTab}
          onChange={setActiveMainTab}
          tabs={MAIN_TABS.filter((tab) => {
            if (tab.id === "payments") return canAccessFinance;
            if (tab.id === "punchCard")
              return punchCardsEnabled && canAccessFinance;
            // Nutrition coaches have no treatment-report loop. The tab is
            // hidden, not removed — /dashboard/patients/:id/treatments/:tid
            // still loads, so nothing that already exists becomes unreachable.
            if (tab.id === "treatments") return !isNutritionClinic;
            return true;
          }).flatMap((tab) =>
            // Nutrition clinics: the ONE entry point to the coaching tools,
            // right after פרופיל. The tools themselves render only on
            // מרכז הליווי.
            tab.id === "profile" && isNutritionClinic
              ? [
                  tab,
                  {
                    id: "nutritionWorkspace",
                    label: "פתח/י את מרכז הליווי",
                    icon: ArrowSquareOut,
                    link: true,
                    onSelect: () => navigate(`/dashboard/nutrition/${patientId}`),
                  },
                ]
              : [tab]
          )}
        />
      </div>

      {!isOwnPatient && !isAdmin && (
        <ReadOnlyBanner
          text={
            canEdit
              ? "המטופל/ת משויך/ת למטפל/ת אחר/ת — יש לך הרשאת עריכה."
              : undefined
          }
        />
      )}

      <div className={`pp-panel main-tab-content pp-panel--${activeMainTab}`}>
        {renderMainTabContent()}
      </div>

      {/* Payment Modal (global - triggered from Payments tab or Appointments Diary tab) */}
      {showPaymentModal &&
        (selectedAppointmentForPayment || selectedTransactionForPayment) && (
        <div
          className="payment-modal-overlay"
          onClick={() => setShowPaymentModal(false)}
        >
          <div className="payment-modal" onClick={(e) => e.stopPropagation()}>
            <div className="payment-modal-header">
              <h3>סימון תשלום</h3>
              <ModalCloseButton onClick={() => setShowPaymentModal(false)} />
            </div>

            <div className="payment-modal-body">
              <div className="payment-apt-info">
                <CalendarBlank size={18} />
                <span>
                  {selectedAppointmentForPayment
                    ? `פגישה מתאריך ${formatAppointmentDateTime(
                        selectedAppointmentForPayment.startTime
                      )}`
                    : selectedTransactionForPayment?.expectedPaymentDate
                    ? `תשלום צפוי לתאריך ${formatAppointmentDateTime(
                        selectedTransactionForPayment.expectedPaymentDate
                      )}`
                    : "עדכון סטטוס תשלום"}
                </span>
              </div>

              <div className="payment-form">
                <div className="form-group">
                  <label>סכום (₪)</label>
                  <input
                    type="number"
                    value={paymentAmount}
                    onChange={(e) => setPaymentAmount(e.target.value)}
                    placeholder="0"
                    min="0"
                  />
                  {hasRateMismatch(paymentAmount) && (
                    <span className="rate-hint">
                      <Info size={14} />
                      התעריף המוגדר למטופל:{" "}
                      {formatCurrency(patient.treatmentRate)}
                    </span>
                  )}
                </div>

                <div className="form-group">
                  <label>אמצעי תשלום</label>
                  <select
                    value={paymentMethod}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                  >
                    <option value="cash">מזומן</option>
                    <option value="credit">אשראי</option>
                    <option value="transfer">העברה בנקאית</option>
                    <option value="check">צ׳ק</option>
                    <option value="bit">אפליקציה (ביט/פייבוקס/אחר)</option>
                  </select>
                </div>
              </div>

              {isUsingGreenInvoice && (
                <div className="invoice-notice">
                  <Receipt size={16} />
                  <span>לאחר סימון התשלום, תוכל להפיק חשבונית</span>
                </div>
              )}
            </div>

            <div className="payment-modal-footer">
              <button
                className="cancel-btn"
                onClick={() => setShowPaymentModal(false)}
              >
                ביטול
              </button>
              <button
                className="confirm-btn"
                onClick={handleConfirmPayment}
                disabled={isProcessingPayment || !paymentAmount}
              >
                {isProcessingPayment ? (
                  <>
                    <LoaderLoop size="xs" />
                    <span>מעבד...</span>
                  </>
                ) : (
                  <>
                    <Check size={18} weight="bold" />
                    <span>סמן כשולם</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Invoice Modal (global) */}
      {showInvoiceModal &&
        invoiceAppointment &&
        (isDigitalInvoiceActive ? (
          <DigitalInvoiceQuickModal
            isOpen={showInvoiceModal}
            onClose={() => {
              setShowInvoiceModal(false);
              setInvoiceAppointment(null);
            }}
            appointment={invoiceAppointment}
            patientName={`${patient.firstName} ${patient.lastName}`}
            amount={invoiceAppointment.actualPaidAmount}
            paymentMethod={invoiceAppointment.paymentMethod}
            onSuccess={() => {
              setShowInvoiceModal(false);
              setInvoiceAppointment(null);
            }}
          />
        ) : (
          <InvoiceQuickModal
            isOpen={showInvoiceModal}
            onClose={() => {
              setShowInvoiceModal(false);
              setInvoiceAppointment(null);
            }}
            appointment={invoiceAppointment}
            patientName={`${patient.firstName} ${patient.lastName}`}
            amount={invoiceAppointment.actualPaidAmount}
            paymentMethod={invoiceAppointment.paymentMethod}
            onSuccess={() => {
              setShowInvoiceModal(false);
              setInvoiceAppointment(null);
            }}
          />
        ))}

      {viewingQuestionnaire && (
        <QuestionnaireViewModal
          submission={viewingQuestionnaire}
          onClose={() => setViewingQuestionnaire(null)}
        />
      )}
    </div>
  );
}

export default PatientProfilePage;
